import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { db } from "../firebase";
import { aiAuthFetch } from "./aiAuthFetch";
import { generateInvoicePDF, generateFabricTailorSheet } from "./campus_invoice";
import { fetchBookingsLight, fetchBookingFull } from "./campus_bookings_light";
import { generateStickerSheetPDF, filterBookingsForStickers, STICKER_STATUS_OPTIONS, effectiveStatus, matchesStatus } from "./campus_stickers";
import { generateInterviewCardsPDF } from "./campus_cards";
import { generateGstRegisterPDF, monthsWithBills, monthLabel, buildRegister } from "./campus_gst_register";
import { StickerColorPickers, useStickerColors } from "./campus_sticker_settings";
import { garmentLines, shadeColor } from "./campus_garment";
import {
    ASSIGNMENT_STAGES,
    assignmentStageLabel,
    isCollectedOrDoneStage,
    isInProgressStage,
    isTailorPackedStage,
    normalizeAssignmentStage,
} from "./campus_assignment_stages";
import CampusHisab from "./campus_hisab";
import CampusQrPay from "./campus_qrpay";
import { buildFabricOrder } from "./campus_fabric_order";
import { downloadFabricOrderPDFs } from "./campus_fabric_order_pdf";
import "./campus_orders.css";

// MUSTAQBIL (campus tailoring) app bookings ke saath shared backend.
// Wahi Firebase project (bluepearl-e9750) jisme admin panel hai.
const CAMPUS_API_BASE = "https://maviinci-ai-bluepearl-e9750.web.app/api";

// Operations data admin ke apne Firestore me (sab devices pe sync).
const COL_INVENTORY = "CampusInventory";   // fabric/cover/tie purchases
const COL_EXPENSES = "CampusExpenses";     // kharch
const COL_ASSIGN = "CampusAssignments";    // per-garment tailor + stage
const COL_TAILORS = "CampusTailors";       // tailor list (naam, phone, active)
const COL_FABRIC_ISSUES = "CampusFabricIssues"; // tailor ko diya gaya kapda (meters)
const COL_ACTIONS = "CampusActionLog";     // har action ka record — Undo ek step isi se peeche jaata hai
const UNDO_CODE = "3624";
const CONFIG_DOC = "CampusConfig";         // consumption rates

const GARMENT_LABEL = { coat: "Coat", pant: "Pant", shirt: "Shirt", vest_coat: "Vest Coat", tie: "Tie" };
const GARMENT_TYPES = ["coat", "pant", "shirt", "vest_coat", "tie"];

// Fabric meters per garment (editable — Firestore CampusConfig me save hote hain).
const DEFAULT_CONSUMPTION = { coat: 3.5, pant: 1.5, shirt: 2.5, vest_coat: 1.0, tie: 0 };

// Campus me koi cutting/stitching nahi chalta — asal me teen haalat hain:
//   pending  → yahan se tailor ko assign hua
//   packed   → tailor ne APP se "Completed" kiya (ban gaya, ABHI USI KE PAAS PADA HAI)
//   received → Founder/Co-Founder ne APP se utha liya
// (cutting/stitching/ready purane values hain — chhode hain taaki purana data na toote)
const STAGES = ASSIGNMENT_STAGES;

const upperFields = [
    { key: "coat_length", label: "Coat" }, { key: "shirt_length", label: "Shirt" },
    { key: "jacket_length", label: "Jacket" }, { key: "vest_coat_length", label: "Vest Coat" },
    { key: "kurta_long_length", label: "Kurta Long" }, { key: "kurta_short_length", label: "Kurta Short" },
    { key: "chest", label: "Chest" }, { key: "stomach", label: "Stomach" }, { key: "hip", label: "Hip" },
    { key: "shoulder", label: "Shoulder" }, { key: "sleeve", label: "Sleeve" }, { key: "neck", label: "Neck" },
    { key: "bicep", label: "Bicep" },
];
const lowerFields = [
    { key: "trouser_length", label: "Trouser Length" }, { key: "waist", label: "Waist" },
    { key: "crotch", label: "Crotch" }, { key: "thigh", label: "Thigh" }, { key: "bottom_opening", label: "Bottom Opening" },
];
const measurementFields = [...upperFields, ...lowerFields];

const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
const fmtINR = (v) => Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0, style: "currency", currency: "INR" });
const fmtM = (v) => `${(Math.round(num(v) * 10) / 10).toLocaleString("en-IN")} m`;
const fmtDate = (v) => {
    if (!v) return "—";
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};
const fmtDateTime = (v) => {
    if (!v) return "—";
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};
const fabricKey = (s) => String(s || "").trim().toLowerCase();
const garmentKey = (bookingId, itemId) => `${bookingId}__${itemId}`;

// ── Stall drives ──
// Ek campus pe stall kuch din chalta hai (jaise IIT Kanpur 1–6 Jul), phir kuch
// hafte baad dobara. Itne din ka gap = alag drive (naya batch).
const DRIVE_GAP_DAYS = 2;
const orderDateOf = (b) => String(b.order_date || b.created_at || "").slice(0, 10);
const fmtDriveRange = (from, to) => {
    const f = new Date(from), t = new Date(to);
    if (Number.isNaN(f.getTime()) || Number.isNaN(t.getTime())) return from || "—";
    const opt = { day: "numeric", month: "short" };
    if (from === to) return f.toLocaleDateString("en-IN", opt);
    if (f.getMonth() === t.getMonth() && f.getFullYear() === t.getFullYear()) {
        return `${f.getDate()}–${t.toLocaleDateString("en-IN", opt)}`;
    }
    return `${f.toLocaleDateString("en-IN", opt)} – ${t.toLocaleDateString("en-IN", opt)}`;
};
// Asli baki raqam: total − advance − delivery pe mila balance. (Booking ka
// remaining_amount field delivery ke baad update nahi hota — stale hai.)
const paidOf = (b) => num(b.advance_amount) + num(b.balance_paid);
const dueOf = (b) => Math.max(0, num(b.total_amount) - paidOf(b));

// Undo ke liye: ops = [{col, id, prev}] — prev null ho to undo me doc delete hota hai,
// warna prev data wapas set (overwrite) hota hai.
const stripId = (obj) => { if (!obj) return null; const { id, ...rest } = obj; return rest; };
const logAction = (label, ops) =>
    db.collection(COL_ACTIONS).add({ label, ops, at: Date.now() }).catch((e) => console.log("action log", e));

const TABS = [
    { id: "orders", label: "Orders" },
    { id: "sales", label: "Fabric Sales" },
    { id: "production", label: "Production" },
    { id: "inventory", label: "Inventory" },
    { id: "payments", label: "Payments" },
    { id: "qrpay", label: "QR Payments" },
    { id: "distribution", label: "Distribution" },
    { id: "tailors", label: "Tailors" },
];

// Fabric 2 type: SUITING (coat/pant/vest me), SHIRTING (shirt me); TIE alag (colour se).
const SALE_CATEGORIES = [
    { id: "suiting", label: "Suiting", types: ["coat", "pant", "vest_coat"], unit: "Fabric" },
    { id: "shirting", label: "Shirting", types: ["shirt"], unit: "Fabric" },
    { id: "tie", label: "Tie", types: ["tie"], unit: "Colour" },
];
const TYPE_OF_CAT = { coat: "suiting", pant: "suiting", vest_coat: "suiting", shirt: "shirting", tie: "tie" };
// Validated categorical palette (dataviz skill) — fixed order, CVD-safe set.
// Console skin: colour sirf SIGNAL hai — garment ki pehchan naam se, rang se nahi
const TYPE_COLOR = { coat: "#3E444C", pant: "#3E444C", shirt: "#3E444C", vest_coat: "#3E444C", tie: "#3E444C" };
// Revenue ko items pe baatne ke weights (invoice split jaisa).
const TYPE_WEIGHT = { coat: 30, pant: 28, shirt: 24, vest_coat: 16, tie: 12 };

function Campus_orders() {
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [tab, setTab] = useState("orders");

    const [inventory, setInventory] = useState([]);
    const [expenses, setExpenses] = useState([]);
    const [assignments, setAssignments] = useState({});
    const [tailors, setTailors] = useState([]);
    const [fabricIssues, setFabricIssues] = useState([]);
    const [drive, setDrive] = useState({ campus: "", from: "", to: "" }); // stall batch filter
    const [driveCustom, setDriveCustom] = useState(false);
    const [consumption, setConsumption] = useState(DEFAULT_CONSUMPTION);
    const [fabricOrder, setFabricOrder] = useState({ shared: {}, lastShareAt: 0 });

    const loadBookings = useCallback(async () => {
        setLoading(true);
        setError("");
        const sortByDate = (list) =>
            list.sort((a, b) => new Date(b.created_at || b.order_date || 0) - new Date(a.created_at || a.order_date || 0));
        try {
            // Light REST read (photos skip — ~3MB vs ~164MB): slow network par bhi fast.
            setBookings(sortByDate(await fetchBookingsLight()));
        } catch (eLight) {
            console.log("campus bookings light read failed, trying full SDK read", eLight);
            try {
                // Fallback 1: SDK full read (bhaari, par kaam karta hai)
                const snap = await db.collection("maviinci_bookings").get();
                setBookings(sortByDate(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
            } catch (e) {
                console.log("campus bookings Firestore read failed, trying API fallback", e);
                // Fallback 2: backend API (agar kabhi Firestore read block ho jaye)
                try {
                    const res = await aiAuthFetch(`${CAMPUS_API_BASE}/bookings`);
                    if (!res.ok) throw new Error(`API ${res.status}`);
                    const data = await res.json();
                    setBookings(sortByDate(Array.isArray(data.bookings) ? data.bookings : []));
                } catch (e2) {
                    console.log("campus orders load failed", e2);
                    setError("Could not load campus orders. Please refresh or try again shortly.");
                }
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadBookings(); }, [loadBookings]);

    // Realtime Firestore listeners (ops data)
    useEffect(() => {
        const u1 = db.collection(COL_INVENTORY).onSnapshot(
            (s) => setInventory(s.docs.map((d) => ({ id: d.id, ...d.data() }))),
            (e) => console.log("inventory listen", e)
        );
        const u2 = db.collection(COL_EXPENSES).onSnapshot(
            (s) => setExpenses(s.docs.map((d) => ({ id: d.id, ...d.data() }))),
            (e) => console.log("expenses listen", e)
        );
        const u3 = db.collection(COL_ASSIGN).onSnapshot(
            (s) => {
                const map = {};
                s.docs.forEach((d) => { map[d.id] = d.data(); });
                setAssignments(map);
            },
            (e) => console.log("assign listen", e)
        );
        const u4 = db.collection(CONFIG_DOC).doc("consumption").onSnapshot(
            (d) => { if (d.exists && d.data()) setConsumption({ ...DEFAULT_CONSUMPTION, ...d.data() }); },
            (e) => console.log("config listen", e)
        );
        const u5 = db.collection(COL_TAILORS).onSnapshot(
            (s) => setTailors(s.docs.map((d) => ({ id: d.id, ...d.data() }))),
            (e) => console.log("tailors listen", e)
        );
        const u6 = db.collection(COL_FABRIC_ISSUES).onSnapshot(
            (s) => setFabricIssues(s.docs.map((d) => ({ id: d.id, ...d.data() }))),
            (e) => console.log("fabric issues listen", e)
        );
        const u7 = db.collection(CONFIG_DOC).doc("fabricOrder").onSnapshot(
            (d) => {
                const data = d.exists ? (d.data() || {}) : {};
                setFabricOrder({ shared: data.shared || {}, lastShareAt: data.lastShareAt || 0 });
            },
            (e) => console.log("fabric order listen", e)
        );
        return () => { u1(); u2(); u3(); u4(); u5(); u6(); u7(); };
    }, []);

    // ── flatten orders into garment units ──
    // ── Drive filter: campus + date range, poore dashboard pe lagta hai ──
    const drives = useMemo(() => {
        const byCampus = new Map();
        bookings.forEach((b) => {
            const d = orderDateOf(b);
            if (!d) return;
            const c = b.campus || "—";
            if (!byCampus.has(c)) byCampus.set(c, []);
            byCampus.get(c).push(d);
        });
        const out = [];
        byCampus.forEach((dates, campus) => {
            const uniq = [...new Set(dates)].sort();
            if (!uniq.length) return;
            const flush = (from, to) => out.push({
                campus, from, to,
                count: dates.filter((d) => d >= from && d <= to).length,
            });
            let start = uniq[0], prev = uniq[0];
            for (let i = 1; i < uniq.length; i++) {
                const gapDays = (new Date(uniq[i]) - new Date(prev)) / 86400000;
                if (gapDays > DRIVE_GAP_DAYS) { flush(start, prev); start = uniq[i]; }
                prev = uniq[i];
            }
            flush(start, prev);
        });
        return out.sort((a, b) => (b.to || "").localeCompare(a.to || ""));
    }, [bookings]);

    const driveOn = !!(drive.campus || drive.from || drive.to);

    const driveBookings = useMemo(() => {
        if (!driveOn) return bookings;
        return bookings.filter((b) => {
            if (drive.campus && b.campus !== drive.campus) return false;
            const d = orderDateOf(b);
            if (drive.from && (!d || d < drive.from)) return false;
            if (drive.to && (!d || d > drive.to)) return false;
            return true;
        });
    }, [bookings, drive, driveOn]);

    // Distribution/Tailors assignments bhi usi drive ke rahein
    const driveAssignments = useMemo(() => {
        if (!driveOn) return assignments;
        const ids = new Set(driveBookings.map((b) => b.id));
        return Object.fromEntries(Object.entries(assignments).filter(([, a]) => ids.has(a.bookingId)));
    }, [assignments, driveBookings, driveOn]);

    const garments = useMemo(() => {
        const out = [];
        driveBookings.forEach((b) => {
            if (b.status === "cancelled") return;   // cancelled: production/inventory/distribution se bahar
            (b.active_items || []).forEach((item) => {
                out.push({
                    key: garmentKey(b.id, item.id),
                    bookingId: b.id,
                    invoiceNo: b.invoice_no || "—",
                    customerName: b.customer_name || "—",
                    campus: b.campus || "—",
                    itemId: item.id,
                    itemType: item.type,
                    itemName: item.name || GARMENT_LABEL[item.type] || item.type,
                    fabric: item.type === "tie" ? (b[`${item.id}_color`] || "") : (b[`${item.id}_fabric`] || ""),
                    shade: b[`${item.id}_shade`] || "",
                    deliveryDate: b.delivery_date || "",
                });
            });
        });
        return out;
    }, [driveBookings]);

    // Aakhri action ko exactly reverse karo — code ke bina nahi.
    const undoLast = async () => {
        const code = window.prompt("Enter the undo code:");
        if (code === null) return;
        if (code.trim() !== UNDO_CODE) { alert("Wrong code."); return; }
        try {
            const snap = await db.collection(COL_ACTIONS).orderBy("at", "desc").limit(1).get();
            if (snap.empty) { alert("Nothing to undo."); return; }
            const docSnap = snap.docs[0];
            const { label, ops = [] } = docSnap.data();
            if (!window.confirm(`Undo last action: "${label}"?`)) return;
            const batch = db.batch();
            ops.forEach((op) => {
                const ref = db.collection(op.col).doc(op.id);
                if (op.prev == null) batch.delete(ref); else batch.set(ref, op.prev);
            });
            batch.delete(docSnap.ref);
            await batch.commit();
            alert(`Undone: ${label}`);
        } catch (e) { console.log("undo", e); alert("Could not undo. Please try again."); }
    };

    return (
        <div className="campus-orders-page">
            <div className="campus-tabs">
                {TABS.map((t) => (
                    <button
                        key={t.id}
                        type="button"
                        className={`campus-tab ${tab === t.id ? "is-active" : ""}`}
                        onClick={() => setTab(t.id)}
                    >
                        {t.label}
                    </button>
                ))}
                <button type="button" className="campus-orders-refresh campus-tab-refresh" onClick={loadBookings} disabled={loading}>
                    {loading ? "Loading…" : "↻ Refresh"}
                </button>
                <button type="button" className="campus-orders-refresh campus-undo-btn" onClick={undoLast} title="Undo the last action (code required)">
                    ↩ Undo
                </button>
            </div>

            {error && <div className="campus-orders-error">{error}</div>}

            <div className="campus-drive-bar">
                <span className="campus-drive-label">Stall drive</span>
                <button
                    type="button"
                    className={`campus-drive-chip ${!driveOn ? "is-on" : ""}`}
                    onClick={() => setDrive({ campus: "", from: "", to: "" })}
                >
                    All campuses<em>{bookings.length}</em>
                </button>
                {drives.map((d) => {
                    const on = drive.campus === d.campus && drive.from === d.from && drive.to === d.to;
                    return (
                        <button
                            type="button"
                            key={`${d.campus}|${d.from}|${d.to}`}
                            className={`campus-drive-chip ${on ? "is-on" : ""}`}
                            onClick={() => setDrive(on ? { campus: "", from: "", to: "" } : { campus: d.campus, from: d.from, to: d.to })}
                            title={`${d.campus} · ${d.from} to ${d.to}`}
                        >
                            {d.campus} · {fmtDriveRange(d.from, d.to)}<em>{d.count}</em>
                        </button>
                    );
                })}
                <button
                    type="button"
                    className={`campus-drive-custom-btn ${driveCustom ? "is-on" : ""}`}
                    onClick={() => setDriveCustom((o) => !o)}
                >
                    Custom ▾
                </button>
                {driveOn && (
                    <span className="campus-drive-count">
                        Showing <strong>{driveBookings.length}</strong> of {bookings.length} orders — every tab below
                    </span>
                )}
            </div>

            {driveCustom && (
                <div className="campus-drive-custom">
                    <select value={drive.campus} onChange={(e) => setDrive((d) => ({ ...d, campus: e.target.value }))}>
                        <option value="">All campuses</option>
                        {[...new Set(bookings.map((b) => b.campus).filter(Boolean))].sort().map((c) => (
                            <option key={c} value={c}>{c}</option>
                        ))}
                    </select>
                    <label>From<input type="date" value={drive.from} onChange={(e) => setDrive((d) => ({ ...d, from: e.target.value }))} /></label>
                    <label>To<input type="date" value={drive.to} onChange={(e) => setDrive((d) => ({ ...d, to: e.target.value }))} /></label>
                    {driveOn && (
                        <button type="button" className="campus-orders-clear" onClick={() => setDrive({ campus: "", from: "", to: "" })}>Clear</button>
                    )}
                </div>
            )}

            {tab === "orders" && <OrdersTab bookings={driveBookings} loading={loading} />}
            {tab === "sales" && <FabricSalesTab bookings={driveBookings} consumption={consumption} />}
            {tab === "production" && <ProductionTab garments={garments} consumption={consumption} />}
            {tab === "inventory" && (
                <InventoryTab
                    garments={garments}
                    bookings={driveBookings}
                    inventory={inventory}
                    consumption={consumption}
                    setConsumption={setConsumption}
                    fabricIssues={fabricIssues}
                    assignments={driveAssignments}
                    fabricOrder={fabricOrder}
                    scopeLabel={driveOn ? `${drive.campus || "All campuses"} · ${fmtDriveRange(drive.from, drive.to)}` : "All stalls"}
                />
            )}
            {tab === "payments" && <PaymentsTab bookings={driveBookings} allBookings={bookings} inventory={inventory} expenses={expenses} drive={drive} />}
            {tab === "qrpay" && <CampusQrPay bookings={bookings} drive={drive} />}
            {tab === "distribution" && <DistributionTab garments={garments} bookings={driveBookings} assignments={driveAssignments} tailors={tailors} fabricIssues={fabricIssues} consumption={consumption} />}
            {tab === "tailors" && <TailorsTab tailors={tailors} assignments={driveAssignments} fabricIssues={fabricIssues} consumption={consumption} />}
        </div>
    );
}

/* ───────────────────────── Tailors tab ─────────────────────────
   Tailor list manage: naam + phone + active/inactive. Assign dropdown
   isi list se aata hai. */
function TailorsTab({ tailors, assignments, fabricIssues = [], consumption = {} }) {
    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [saving, setSaving] = useState(false);

    // Issue/return-fabric form
    const [issForm, setIssForm] = useState({ mode: "issue", tailor: "", fabric: "", shade: "", meters: "", note: "" });
    const [issSaving, setIssSaving] = useState(false);
    const [addOpen, setAddOpen] = useState(false);
    const [ledgerOpen, setLedgerOpen] = useState(false);
    const [histFor, setHistFor] = useState(null);
    const [packedFor, setPackedFor] = useState(null); // kis tailor ke packed pieces ka popup khula hai
    const [fabOpen, setFabOpen] = useState({}); // tailor name → fabric list expanded?
    const [viewTailor, setViewTailor] = useState(""); // "" = All — switcher se ek tailor full-screen
    const [pieceView, setPieceView] = useState(null); // { tailor, type, group, label } — pieces popup
    const issueRef = useRef(null);

    // Popup ke liye: chune gaye tailor ke PACKED pieces (naya sabse upar)
    const packedList = useMemo(() => {
        if (!packedFor) return [];
        return Object.entries(assignments)
            .filter(([, a]) => a.tailor === packedFor && isTailorPackedStage(a.stage))
            .map(([key, a]) => ({ key, ...a }))
            .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
    }, [packedFor, assignments]);

    // Har tailor ke paas kitne pieces assigned hain + est. fabric need (naam se)
    const loadByName = useMemo(() => {
        const m = {};
        Object.values(assignments).forEach((a) => {
            if (!a.tailor) return;
            if (!m[a.tailor]) m[a.tailor] = { pieces: 0, estMeters: 0 };
            m[a.tailor].pieces += 1;
            m[a.tailor].estMeters += consumption[a.itemType] || 0;
        });
        return m;
    }, [assignments, consumption]);

    // Har tailor ko kitna kapda diya (issued meters)
    const issuedByName = useMemo(() => {
        const m = {};
        fabricIssues.forEach((f) => { if (f.tailor) m[f.tailor] = (m[f.tailor] || 0) + num(f.meters); });
        return m;
    }, [fabricIssues]);

    // Per-tailor, per-fabric account: diya (issued) − use hua (pieces × rate) = tailor ke paas bacha.
    // Saath me garment breakdown (kitne coat/pant/shirt, kitne packed).
    const tailorAccounts = useMemo(() => {
        const fabKeyOf = (fabric, shade) => `${fabricKey(fabric) || "—"}||${fabricKey(shade)}`;
        const acc = {};
        const ensure = (t) => {
            if (!acc[t]) acc[t] = { fabrics: {}, types: {} };
            return acc[t];
        };
        Object.values(assignments).forEach((a) => {
            if (!a.tailor) return;
            const t = ensure(a.tailor);
            if (!t.types[a.itemType]) t.types[a.itemType] = { total: 0, packed: 0, received: 0 };
            t.types[a.itemType].total += 1;
            const st = normalizeAssignmentStage(a.stage);
            if (isTailorPackedStage(st)) t.types[a.itemType].packed += 1;
            else if (isCollectedOrDoneStage(st)) t.types[a.itemType].received += 1;
            if (a.itemType === "tie") return; // tie me fabric-meters nahi lagte
            const k = fabKeyOf(a.fabric, a.shade);
            if (!t.fabrics[k]) t.fabrics[k] = { fabric: a.fabric || "(No fabric)", shade: a.shade || "", given: 0, usedM: 0, pieces: 0 };
            t.fabrics[k].usedM += consumption[a.itemType] || 0;
            t.fabrics[k].pieces += 1;
        });
        fabricIssues.forEach((f) => {
            if (!f.tailor) return;
            const t = ensure(f.tailor);
            const k = fabKeyOf(f.fabric, f.shade);
            if (!t.fabrics[k]) t.fabrics[k] = { fabric: f.fabric || "(No fabric)", shade: f.shade || "", given: 0, usedM: 0, pieces: 0 };
            t.fabrics[k].given += num(f.meters);
        });
        return acc;
    }, [assignments, fabricIssues, consumption]);

    const fabricNames = useMemo(
        () => [...new Set(fabricIssues.map((f) => f.fabric).filter(Boolean))].sort(),
        [fabricIssues]
    );

    const addTailor = async () => {
        const n = name.trim();
        if (!n) { alert("Please enter the tailor's name."); return; }
        if (tailors.some((t) => (t.name || "").trim().toLowerCase() === n.toLowerCase())) {
            alert("This tailor already exists."); return;
        }
        setSaving(true);
        try {
            const ref = db.collection(COL_TAILORS).doc();
            await ref.set({ name: n, phone: phone.trim(), active: true, createdAt: Date.now() });
            logAction(`Add tailor · ${n}`, [{ col: COL_TAILORS, id: ref.id, prev: null }]);
            setName(""); setPhone("");
        } catch (e) { console.log("add tailor", e); alert("Could not save. Please try again."); }
        finally { setSaving(false); }
    };

    const toggleActive = async (t) => {
        try {
            await db.collection(COL_TAILORS).doc(t.id).set({ active: !(t.active !== false) }, { merge: true });
            logAction(`${t.active !== false ? "Deactivate" : "Activate"} tailor · ${t.name}`, [{ col: COL_TAILORS, id: t.id, prev: stripId({ ...t }) }]);
        }
        catch (e) { console.log("toggle tailor", e); }
    };

    const removeTailor = async (t) => {
        if (!window.confirm(`Remove ${t.name} from the list?`)) return;
        try {
            await db.collection(COL_TAILORS).doc(t.id).delete();
            logAction(`Remove tailor · ${t.name}`, [{ col: COL_TAILORS, id: t.id, prev: stripId({ ...t }) }]);
        }
        catch (e) { console.log("del tailor", e); }
    };

    const addIssue = async () => {
        const isReturn = issForm.mode === "return";
        const m = num(issForm.meters);
        if (!issForm.tailor) { alert("Select a tailor."); return; }
        if (!(m > 0)) { alert(isReturn ? "Enter the meters of fabric returned." : "Enter the meters of fabric given."); return; }
        // Return pe warning agar tailor ke paas utna kapda hi nahi hai
        if (isReturn) {
            const k = `${fabricKey(issForm.fabric) || "—"}||${fabricKey(issForm.shade)}`;
            const fabAcc = tailorAccounts[issForm.tailor]?.fabrics?.[k];
            const left = fabAcc ? fabAcc.given - fabAcc.usedM : 0;
            if (m > left && !window.confirm(`This tailor currently holds only ${fmtM(Math.max(0, left))} of this fabric — log a return of ${fmtM(m)} anyway?`)) return;
        }
        setIssSaving(true);
        try {
            const ref = db.collection(COL_FABRIC_ISSUES).doc();
            await ref.set({
                tailor: issForm.tailor,
                fabric: issForm.fabric.trim() || "(No fabric)",
                shade: issForm.shade.trim(),
                meters: isReturn ? -m : m, // return = negative (hisaab khud adjust)
                kind: isReturn ? "return" : "issue",
                note: issForm.note.trim() || (isReturn ? "Fabric returned" : ""),
                date: new Date().toISOString(),
                createdAt: Date.now(),
            });
            logAction(`${isReturn ? "Return" : "Issue"} ${fmtM(m)} · ${issForm.tailor}`, [{ col: COL_FABRIC_ISSUES, id: ref.id, prev: null }]);
            setIssForm({ mode: issForm.mode, tailor: issForm.tailor, fabric: "", shade: "", meters: "", note: "" });
        } catch (e) { console.log("add issue", e); alert("Could not save. Please try again."); }
        finally { setIssSaving(false); }
    };

    // Account card se seedha return — bacha hua kapda wapas lena
    const returnFromCard = async (tName, f) => {
        const left = f.given - f.usedM;
        const raw = window.prompt(
            `Return fabric from ${tName} — ${f.fabric}${f.shade ? ` · Shade ${f.shade}` : ""} (meters):`,
            left > 0 ? String(Math.round(left * 10) / 10) : ""
        );
        if (raw === null) return;
        const m = num(raw);
        if (!(m > 0)) { alert("Enter a valid number of meters."); return; }
        if (m > left && !window.confirm(`This tailor currently holds only ${fmtM(Math.max(0, left))} — log a return of ${fmtM(m)} anyway?`)) return;
        try {
            const ref = db.collection(COL_FABRIC_ISSUES).doc();
            await ref.set({
                tailor: tName, fabric: f.fabric, shade: f.shade,
                meters: -m, kind: "return", note: "Fabric returned",
                date: new Date().toISOString(), createdAt: Date.now(),
            });
            logAction(`Return ${fmtM(m)} ${f.fabric} · ${tName}`, [{ col: COL_FABRIC_ISSUES, id: ref.id, prev: null }]);
        } catch (e) { console.log("return fabric", e); alert("Could not save. Please try again."); }
    };

    const removeIssue = async (f) => {
        if (!window.confirm(`Delete this fabric entry (${f.tailor} · ${fmtM(f.meters)})?`)) return;
        try {
            await db.collection(COL_FABRIC_ISSUES).doc(f.id).delete();
            logAction(`Delete ledger entry · ${f.tailor} (${fmtM(f.meters)})`, [{ col: COL_FABRIC_ISSUES, id: f.id, prev: stripId({ ...f }) }]);
        }
        catch (e) { console.log("del issue", e); }
    };

    const sorted = [...tailors].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    const totalIssued = fabricIssues.reduce((a, f) => a + num(f.meters), 0);
    const issueLog = [...fabricIssues].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

    const totals = useMemo(() => {
        let pieces = 0, packed = 0, received = 0, used = 0, coats = 0;
        const byType = {};
        Object.values(tailorAccounts).forEach((a) => {
            GARMENT_TYPES.forEach((ty) => {
                const t = a.types[ty];
                if (t) {
                    pieces += t.total;
                    packed += t.packed;
                    received += t.received || 0;
                    if (!byType[ty]) byType[ty] = { total: 0, received: 0 };
                    byType[ty].total += t.total;
                    byType[ty].received += t.received || 0;
                    if (ty === "coat") coats += t.total;
                }
            });
            Object.values(a.fabrics).forEach((f) => { used += f.usedM; });
        });
        return { pieces, packed, received, coats, byType, used, withTailors: totalIssued - used };
    }, [tailorAccounts, totalIssued]);

    // Status popup: chune gaye tailor+type+group ke pieces, fabric+shade wise grouped
    const pieceViewGroups = useMemo(() => {
        if (!pieceView) return [];
        const match = (a) => {
            if (a.tailor !== pieceView.tailor || a.itemType !== pieceView.type) return false;
            const st = normalizeAssignmentStage(a.stage);
            if (pieceView.group === "received") return isCollectedOrDoneStage(st);
            if (pieceView.group === "collect") return isTailorPackedStage(st);
            return isInProgressStage(st);
        };
        const m = new Map();
        Object.values(assignments).forEach((a) => {
            if (!match(a)) return;
            const k = `${a.fabric || "(No fabric)"}||${a.shade || ""}`;
            if (!m.has(k)) m.set(k, { fabric: a.fabric || "(No fabric)", shade: a.shade || "", items: [] });
            m.get(k).items.push(a);
        });
        return [...m.values()].sort((x, y) => y.items.length - x.items.length);
    }, [pieceView, assignments]);

    const fabricShort = totals.withTailors < 0;
    const fabricSub = fabricShort
        ? `${fmtM(Math.abs(totals.withTailors))} short`
        : `${fmtM(Math.max(0, totals.withTailors))} left`;
    const activeTailors = sorted.filter((t) => t.active !== false).length;

    return (
        <div className="campus-panel">
            <div className="campus-tsticky">
                <div className="campus-tsticky-grid">
                    <div className="campus-tmetric">
                        <span className="campus-tmetric-label">Tailors</span>
                        <strong className="campus-tmetric-val">{String(activeTailors).padStart(2, "0")}</strong>
                        <span className="campus-tmetric-sub">active</span>
                    </div>
                    <div className="campus-tmetric campus-tmetric--work">
                        <span className="campus-tmetric-label">Pieces with tailors</span>
                        <strong className="campus-tmetric-val">{totals.pieces}</strong>
                        <div className="campus-tmetric-types">
                            {GARMENT_TYPES.filter((ty) => totals.byType[ty]?.total).map((ty) => (
                                <span className="campus-tmetric-type" key={ty} title={`${GARMENT_LABEL[ty]}: ${totals.byType[ty].received || 0} received of ${totals.byType[ty].total}`}>
                                    <i style={{ background: TYPE_COLOR[ty] }} />
                                    {GARMENT_LABEL[ty]}
                                    <b>{totals.byType[ty].received || 0}/{totals.byType[ty].total}</b>
                                </span>
                            ))}
                        </div>
                    </div>
                    <div className="campus-tmetric">
                        <span className="campus-tmetric-label">To collect</span>
                        <strong className="campus-tmetric-val">{String(totals.packed).padStart(2, "0")}</strong>
                        <span className="campus-tmetric-sub">at tailor</span>
                    </div>
                    <div className="campus-tmetric campus-tmetric--good">
                        <span className="campus-tmetric-label">Received</span>
                        <strong className="campus-tmetric-val">{String(totals.received).padStart(2, "0")}</strong>
                        <span className="campus-tmetric-sub">collected</span>
                    </div>
                    <div className={`campus-tmetric ${fabricShort ? "campus-tmetric--bad" : "campus-tmetric--focus"}`}>
                        <span className="campus-tmetric-label">Fabric given</span>
                        <strong className="campus-tmetric-val">{fmtM(totalIssued)}</strong>
                        <span className={`campus-tmetric-sub ${fabricShort ? "is-bad" : "is-ok"}`}>{fabricSub}</span>
                    </div>
                </div>
            </div>

            <div className="campus-tprof-bar">
                <div className="campus-table-title" style={{ margin: 0 }}>Tailors ({sorted.length})</div>
                <button type="button" className="campus-bulk-btn" onClick={() => setAddOpen((o) => !o)}>{addOpen ? "Close" : "+ Add tailor"}</button>
            </div>
            {addOpen && (
                <div className="campus-purchase-form" style={{ marginBottom: 14 }}>
                    <input placeholder="Tailor name" value={name}
                        onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addTailor()} />
                    <input placeholder="Phone (optional)" value={phone} inputMode="numeric"
                        onChange={(e) => setPhone(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addTailor()} />
                    <button type="button" onClick={addTailor} disabled={saving}>{saving ? "Saving…" : "+ Add"}</button>
                </div>
            )}

            {sorted.length > 0 && (
                <div className="campus-tswitch">
                    <button type="button" className={viewTailor === "" ? "is-active" : ""} onClick={() => setViewTailor("")}>
                        All ({sorted.length})
                    </button>
                    {sorted.map((t) => (
                        <button type="button" key={t.id}
                            className={`${viewTailor === t.name ? "is-active" : ""} ${t.active === false ? "is-off" : ""}`}
                            onClick={() => setViewTailor(viewTailor === t.name ? "" : t.name)}>
                            {t.name}
                        </button>
                    ))}
                </div>
            )}

            {!sorted.length ? (
                <div className="campus-orders-empty">No tailors yet — add one above.</div>
            ) : viewTailor && !sorted.some((t) => t.name === viewTailor) ? (
                <div className="campus-orders-empty">Tailor not found.</div>
            ) : (
                <div className={`campus-tprof-grid ${viewTailor ? "campus-tprof-grid--single" : ""}`}>
                    {(viewTailor ? sorted.filter((t) => t.name === viewTailor) : sorted).map((t) => {
                        const active = t.active !== false;
                        const acct = tailorAccounts[t.name] || { fabrics: {}, types: {} };
                        const load = loadByName[t.name] || { pieces: 0, estMeters: 0 };
                        const givenM = issuedByName[t.name] || 0;
                        // packed = ban gaya par ABHI TAILOR KE PAAS PADA HAI (uthana baaki)
                        // received = Founder utha ke le gaya
                        const packedCount = GARMENT_TYPES.reduce((a, ty) => a + (acct.types[ty]?.packed || 0), 0);
                        const receivedCount = GARMENT_TYPES.reduce((a, ty) => a + (acct.types[ty]?.received || 0), 0);
                        const doneCount = packedCount + receivedCount;
                        const pct = load.pieces ? Math.round((doneCount / load.pieces) * 100) : 0;
                        const fabRows = Object.values(acct.fabrics).sort((a, b) => (a.given - a.usedM) - (b.given - b.usedM));
                        const shortCount = fabRows.filter((f) => f.given - f.usedM < -0.05).length;
                        const typeRows = GARMENT_TYPES.filter((ty) => acct.types[ty]?.total);
                        const initials = (t.name || "?").trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
                        const showHist = histFor === t.name || !!viewTailor;
                        const hist = showHist ? issueLog.filter((f) => f.tailor === t.name).slice(0, viewTailor ? 15 : 6) : [];
                        const isFabOpen = !!fabOpen[t.name] || !!viewTailor;
                        const balance = givenM - load.estMeters;
                        return (
                            <div className={`campus-tprof ${active ? "" : "campus-tprof--off"}`} key={t.id}>
                                <div className="campus-tprof-head">
                                    <div className="campus-tprof-ava">{initials}</div>
                                    <div className="campus-tprof-id">
                                        <strong>{t.name}</strong>
                                        {t.phone ? <a href={`tel:${t.phone}`}>📞 {t.phone}</a> : <span>No phone</span>}
                                    </div>
                                    <div className="campus-tprof-actions">
                                        <button type="button" className={`campus-toggle ${active ? "on" : "off"}`} onClick={() => toggleActive(t)}>
                                            {active ? "Active" : "Inactive"}
                                        </button>
                                        {!viewTailor && (
                                            <button type="button" className="campus-del" title="Remove tailor" onClick={() => removeTailor(t)}>✕</button>
                                        )}
                                    </div>
                                </div>

                                <div className="campus-tprof-hero">
                                    <div className="campus-tprof-hero-cell">
                                        <span className="campus-tprof-hero-label">Pieces</span>
                                        <strong className="campus-tprof-hero-val">{load.pieces}</strong>
                                    </div>
                                    <div className="campus-tprof-hero-cell campus-tprof-hero-cell--gold">
                                        <span className="campus-tprof-hero-label">Fabric given</span>
                                        <strong className="campus-tprof-hero-val">{fmtM(givenM)}</strong>
                                        <span className={`campus-tprof-hero-sub ${balance < 0 ? "is-short" : ""}`}>
                                            {balance < 0 ? `${fmtM(Math.abs(balance))} short vs need` : `${fmtM(Math.max(0, balance))} left vs need`}
                                        </span>
                                    </div>
                                </div>

                                <div className="campus-tprof-status">
                                    <button type="button" className={`campus-tprof-st ${packedCount ? "is-click" : ""}`}
                                        disabled={!packedCount}
                                        title="Finished but still with tailor"
                                        onClick={() => packedCount && setPackedFor(t.name)}>
                                        <b>{packedCount}</b> to collect
                                    </button>
                                    <span className="campus-tprof-st"><b>{receivedCount}</b> received</span>
                                    <span className="campus-tprof-st"><b>{pct}%</b> made</span>
                                </div>
                                {load.pieces > 0 && (
                                    <div className="campus-type-bar campus-tprof-barline"><div style={{ width: `${pct}%`, background: "#0F7B3E" }} /></div>
                                )}

                                <div className="campus-tprof-work">
                                    <div className="campus-tprof-sec-label">Work assigned</div>
                                    {typeRows.length ? typeRows.map((ty) => {
                                        const cnt = acct.types[ty].total;
                                        const needM = ty === "tie" ? 0 : cnt * (consumption[ty] || 0);
                                        return (
                                            <div className="campus-tprof-work-row" key={ty}>
                                                <span className="campus-tprof-work-dot" style={{ background: TYPE_COLOR[ty] }} />
                                                <span className="campus-tprof-work-name" style={{ color: TYPE_COLOR[ty] }}>
                                                    {GARMENT_LABEL[ty]}{cnt > 1 ? "s" : ""}
                                                </span>
                                                <b className="campus-tprof-work-n">{cnt}</b>
                                                {needM > 0 && <span className="campus-tprof-work-m">{fmtM(needM)} needed</span>}
                                            </div>
                                        );
                                    }) : <div className="campus-tprof-empty">No work assigned</div>}
                                </div>

                                <div className="campus-tprof-fabrics">
                                    <button type="button" className="campus-tprof-fabtoggle"
                                        onClick={() => setFabOpen((o) => ({ ...o, [t.name]: !o[t.name] }))}
                                        disabled={!fabRows.length}>
                                        <span className="campus-tprof-sec-label">Fabric ledger</span>
                                        <span className="campus-tprof-fabsum">
                                            {fabRows.length
                                                ? `${fabRows.length} fabric${fabRows.length > 1 ? "s" : ""}${shortCount ? ` · ${shortCount} short` : ""}`
                                                : "Nothing issued"}
                                        </span>
                                        {fabRows.length > 0 && <span className="campus-tprof-chev">{isFabOpen ? "▴" : "▾"}</span>}
                                    </button>

                                    {isFabOpen && fabRows.length > 0 && (
                                        <div className="campus-fablgr-wrap">
                                            <table className="campus-table campus-fablgr">
                                                <thead>
                                                    <tr>
                                                        <th>Fabric</th>
                                                        <th className="r">Pcs</th>
                                                        <th className="r">Given</th>
                                                        <th className="r">Need</th>
                                                        <th className="r">Balance</th>
                                                        <th aria-label="Return" />
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {fabRows.map((f) => {
                                                        const left = f.given - f.usedM;
                                                        return (
                                                            <tr key={`${f.fabric}|${f.shade}`}>
                                                                <td className="campus-fablgr-name">
                                                                    <strong>{f.fabric}</strong>
                                                                    {f.shade && <span className="campus-shade-pill">{f.shade}</span>}
                                                                </td>
                                                                <td className="r">{f.pieces || "—"}</td>
                                                                <td className="r">{fmtM(f.given)}</td>
                                                                <td className="r campus-fablgr-need">{fmtM(f.usedM)}</td>
                                                                <td className="r">
                                                                    <span className={`campus-stock campus-stock--nowrap ${left < -0.05 ? "campus-stock--neg" : left > 0.05 ? "campus-stock--ok" : "campus-stock--zero"}`}>
                                                                        {left < -0.05 ? `${fmtM(Math.abs(left))} short` : left > 0.05 ? `${fmtM(left)} extra` : "settled"}
                                                                    </span>
                                                                </td>
                                                                <td className="c">
                                                                    {left > 0 && (
                                                                        <button type="button" className="campus-sheet-btn" title="Take leftover back"
                                                                            onClick={() => returnFromCard(t.name, f)}>↩</button>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>

                                {!!viewTailor && (
                                    <div className="campus-tprof-stages">
                                        <div className="campus-tprof-sec-label">Status by garment</div>
                                        {typeRows.length ? typeRows.map((ty) => {
                                            const tt = acct.types[ty];
                                            const inprog = tt.total - (tt.packed || 0) - (tt.received || 0);
                                            const cells = [
                                                { group: "inprog", label: "In progress", n: inprog, cls: "st-prog" },
                                                { group: "collect", label: "To collect", n: tt.packed || 0, cls: "st-coll" },
                                                { group: "received", label: "Received", n: tt.received || 0, cls: "st-recv" },
                                            ];
                                            return (
                                                <div className="campus-stage-block" key={ty}>
                                                    <div className="campus-stage-block-head">
                                                        <span className="campus-tprof-work-dot" style={{ background: TYPE_COLOR[ty] }} />
                                                        <strong>{GARMENT_LABEL[ty]}{tt.total > 1 ? "s" : ""}</strong>
                                                        <span className="campus-stage-total">{tt.total}</span>
                                                    </div>
                                                    <div className="campus-stage-cells">
                                                        {cells.map((c) => (
                                                            <button type="button" key={c.group}
                                                                className={`campus-stage-cell ${c.cls} ${c.n ? "" : "is-zero"}`}
                                                                disabled={!c.n}
                                                                onClick={() => setPieceView({ tailor: t.name, type: ty, group: c.group, label: `${GARMENT_LABEL[ty]} — ${c.label}` })}>
                                                                <b>{c.n}</b>
                                                                <span>{c.label}</span>
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        }) : <div className="campus-tprof-empty">No work assigned</div>}
                                        <p className="campus-note" style={{ marginTop: 6 }}>Click a count to see its pieces (fabric-wise, with invoice numbers).</p>
                                    </div>
                                )}

                                <div className="campus-tprof-foot">
                                    <button type="button" className="campus-sheet-btn campus-sheet-btn--pri" onClick={() => {
                                        setIssForm((prev) => ({ ...prev, mode: "issue", tailor: t.name }));
                                        issueRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                                    }}>+ Issue fabric</button>
                                    <button type="button" className="campus-sheet-btn" onClick={() => setHistFor(histFor === t.name ? null : t.name)}>
                                        {histFor === t.name ? "Hide history" : "History"}
                                    </button>
                                </div>

                                {showHist && (
                                    <div className="campus-tprof-hist">
                                        {hist.length ? hist.map((f) => (
                                            <div className="campus-tprof-hist-row" key={f.id}>
                                                <span>{fmtDate(f.date)}</span>
                                                <span className="campus-tprof-hist-fab">{f.fabric}{f.shade ? ` · ${f.shade}` : ""}</span>
                                                <strong className={num(f.meters) < 0 ? "campus-ret-strong" : ""}>{fmtM(f.meters)}</strong>
                                            </div>
                                        )) : <div className="campus-tprof-empty">No entries yet</div>}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
            <p className="campus-note">Work meters = pieces × consumption. Fabric balance = given − estimated use. Expand “Fabric ledger” for per-fabric detail. ↩ returns leftover. Inactive tailors stay hidden from assign dropdowns.</p>

            {/* 📦 Packed pieces popup — kaun se invoice ke kaun se pieces packed hain */}
            {packedFor && (
                <div className="campus-detail-overlay" onClick={() => setPackedFor(null)}>
                    <div className="campus-detail-modal campus-remaining-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="campus-detail-header">
                            <div>
                                <div className="campus-detail-kicker">Finished · waiting pickup</div>
                                <h3>{packedFor} · {packedList.length} to collect</h3>
                            </div>
                            <div className="campus-detail-header-right">
                                <button type="button" className="campus-detail-close" onClick={() => setPackedFor(null)} aria-label="Close">&times;</button>
                            </div>
                        </div>
                        <div className="campus-detail-body">
                            {!packedList.length ? (
                                <div className="campus-orders-empty">Nothing waiting to be collected.</div>
                            ) : packedList.map((a) => (
                                <div className="campus-remaining-row" key={a.key}>
                                    <div className="campus-remaining-info">
                                        <strong>{a.invoiceNo || "—"}</strong>
                                        <span className="campus-order-by">{a.customerName || ""}</span>
                                    </div>
                                    <span className="campus-remaining-count">{a.itemName || GARMENT_LABEL[a.itemType] || a.itemType}</span>
                                    <span className="campus-missing">
                                        {[a.fabric, a.shade ? `Shade ${a.shade}` : ""].filter(Boolean).join(" · ")}
                                        {a.updatedAt ? ` · ${fmtDateTime(a.updatedAt)}` : ""}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {pieceView && (
                <div className="campus-detail-overlay" onClick={() => setPieceView(null)}>
                    <div className="campus-detail-modal campus-remaining-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="campus-detail-header">
                            <div>
                                <div className="campus-detail-kicker">{pieceView.tailor}</div>
                                <h3>{pieceView.label} · {pieceViewGroups.reduce((a, g) => a + g.items.length, 0)} pcs</h3>
                            </div>
                            <div className="campus-detail-header-right">
                                <button type="button" className="campus-detail-close" onClick={() => setPieceView(null)} aria-label="Close">&times;</button>
                            </div>
                        </div>
                        <div className="campus-detail-body">
                            {!pieceViewGroups.length ? (
                                <div className="campus-orders-empty">No pieces here.</div>
                            ) : (
                                pieceViewGroups.map((g) => (
                                    <div className="campus-piece-group" key={`${g.fabric}|${g.shade}`}>
                                        <div className="campus-piece-group-head">
                                            <strong>{g.fabric}</strong>
                                            {g.shade && <span className="campus-shade-pill">{g.shade}</span>}
                                            <span className="campus-piece-count">{g.items.length} pcs</span>
                                        </div>
                                        <div className="campus-piece-chips">
                                            {g.items.map((a, i) => (
                                                <span className="campus-piece-chip" key={`${a.invoiceNo}-${a.itemId || i}`}
                                                    title={`${a.customerName || ""} · ${a.itemName || ""} · stage: ${assignmentStageLabel(a.stage)}`}>
                                                    <b>{a.invoiceNo}</b>
                                                    {a.customerName ? ` ${a.customerName.split(" ")[0]}` : ""}
                                                    {pieceView.group === "inprog" && <i> · {assignmentStageLabel(a.stage)}</i>}
                                                    {pieceView.group === "received" && normalizeAssignmentStage(a.stage) === "box_packed" && (
                                                        <i> · packed</i>
                                                    )}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            )}

            <div ref={issueRef} className="campus-table-title">Issue / return fabric</div>
            <div className="campus-purchase-form">
                <div className="campus-seg">
                    <button type="button" className={issForm.mode === "issue" ? "is-active" : ""}
                        onClick={() => setIssForm({ ...issForm, mode: "issue" })}>Issue</button>
                    <button type="button" className={issForm.mode === "return" ? "is-active" : ""}
                        onClick={() => setIssForm({ ...issForm, mode: "return" })}>Return</button>
                </div>
                <select value={issForm.tailor} onChange={(e) => setIssForm({ ...issForm, tailor: e.target.value })}>
                    <option value="">— Select tailor —</option>
                    {sorted.filter((t) => t.active !== false).map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
                </select>
                <input list="campus-issue-fabrics" placeholder="Fabric name" value={issForm.fabric}
                    onChange={(e) => setIssForm({ ...issForm, fabric: e.target.value })} />
                <datalist id="campus-issue-fabrics">
                    {fabricNames.map((n) => <option key={n} value={n} />)}
                </datalist>
                <input placeholder="Shade" value={issForm.shade} style={{ flex: "0 1 90px" }}
                    onChange={(e) => setIssForm({ ...issForm, shade: e.target.value })} />
                <input type="number" min="0" step="0.5" placeholder="Meters" value={issForm.meters}
                    onChange={(e) => setIssForm({ ...issForm, meters: e.target.value })} />
                <input placeholder="Note (optional)" value={issForm.note}
                    onChange={(e) => setIssForm({ ...issForm, note: e.target.value })} />
                <button type="button" onClick={addIssue} disabled={issSaving}>
                    {issSaving ? "Saving…" : (issForm.mode === "return" ? "↩ Return" : "+ Issue")}
                </button>
            </div>

            <div className="campus-tprof-bar" style={{ marginTop: 22 }}>
                <div className="campus-table-title" style={{ margin: 0 }}>Fabric ledger ({fmtM(totalIssued)} net with tailors)</div>
                <button type="button" className="campus-sheet-btn" onClick={() => setLedgerOpen((o) => !o)}>{ledgerOpen ? "Hide full ledger" : "Show full ledger"}</button>
            </div>
            {ledgerOpen && (
            <div className="campus-table-wrap">
                <table className="campus-table">
                    <thead><tr><th>Date</th><th>Tailor</th><th>Fabric</th><th className="c">Shade</th><th className="r">Meters</th><th>Note</th><th></th></tr></thead>
                    <tbody>
                        {issueLog.length ? issueLog.map((f) => {
                            const isReturn = num(f.meters) < 0;
                            return (
                                <tr key={f.id}>
                                    <td>{fmtDate(f.date)}</td>
                                    <td>
                                        <strong>{f.tailor}</strong>
                                        {isReturn && <span className="campus-ret-pill">↩ Return</span>}
                                    </td>
                                    <td>{f.fabric || "—"}</td>
                                    <td className="c">{f.shade ? <span className="campus-shade-pill">{f.shade}</span> : "—"}</td>
                                    <td className={`r ${isReturn ? "campus-ret-meters" : ""}`}><strong>{fmtM(f.meters)}</strong></td>
                                    <td>{f.note || "—"}</td>
                                    <td className="c"><button type="button" className="campus-del" onClick={() => removeIssue(f)}>✕</button></td>
                                </tr>
                            );
                        }) : <tr><td colSpan={7} className="c">No fabric issued yet. Entries are added here manually or automatically from bulk assign.</td></tr>}
                    </tbody>
                </table>
            </div>
            )}
        </div>
    );
}

/* ───────────────────────── Fabric Sales tab ─────────────────────────
   Kaunsa fabric kitna bika — pivot: fabric × garment type. Shade ignore.
   Metric: Pieces / Meters / Revenue. */
const METRICS = [
    { id: "pieces", label: "Pieces" },
    { id: "meters", label: "Meters" },
    { id: "revenue", label: "Revenue" },
];

function FabricSalesTab({ bookings, consumption }) {
    const [category, setCategory] = useState("suiting");
    const [metric, setMetric] = useState("pieces");
    const [view, setView] = useState("chart"); // chart | table
    const [hover, setHover] = useState(null);

    // Ties fabric-meters me nahi aate → us category me Meters metric N/A.
    const metrics = category === "tie" ? METRICS.filter((m) => m.id !== "meters") : METRICS;
    const activeMetric = metrics.some((m) => m.id === metric) ? metric : "pieces";

    const fmtVal = useCallback((v) => {
        if (activeMetric === "revenue") return fmtINR(v);
        if (activeMetric === "meters") return fmtM(v);
        return `${Math.round(v)}`;
    }, [activeMetric]);

    // Sab categories ka aggregation ek hi baar. Suiting fabric coat/pant/vest me,
    // shirting fabric shirt me, tie colour se group hota hai.
    const data = useMemo(() => {
        const cats = { suiting: {}, shirting: {}, tie: {} };
        bookings.forEach((b) => {
            const items = (b.active_items || []).filter((i) => i && i.type);
            const wSum = items.reduce((a, i) => a + (TYPE_WEIGHT[i.type] || 20), 0) || 1;
            const total = num(b.total_amount);
            items.forEach((item) => {
                const catId = TYPE_OF_CAT[item.type];
                if (!catId) return;
                const isTie = catId === "tie";
                const name = (isTie ? b[`${item.id}_color`] : b[`${item.id}_fabric`]) || (isTie ? "(No colour)" : "(No fabric)");
                const k = fabricKey(name) || "—";
                const store = cats[catId];
                if (!store[k]) store[k] = { name, pieces: {}, meters: {}, revenue: {} };
                store[k].pieces[item.type] = (store[k].pieces[item.type] || 0) + 1;
                store[k].meters[item.type] = (store[k].meters[item.type] || 0) + (consumption[item.type] || 0);
                store[k].revenue[item.type] = (store[k].revenue[item.type] || 0) + total * (TYPE_WEIGHT[item.type] || 20) / wSum;
            });
        });
        return cats;
    }, [bookings, consumption]);

    const cat = SALE_CATEGORIES.find((c) => c.id === category);
    const types = cat.types;

    const rows = useMemo(() => {
        const store = data[category] || {};
        return Object.values(store)
            .map((r) => ({ name: r.name, all: r, total: types.reduce((a, t) => a + (r[activeMetric][t] || 0), 0) }))
            .filter((r) => r.total > 0)
            .sort((a, b) => b.total - a.total);
    }, [data, category, types, activeMetric]);

    const { typeTotals, grand, kpis } = useMemo(() => {
        const store = data[category] || {};
        const typeTotals = {}; let grand = 0;
        types.forEach((t) => { typeTotals[t] = Object.values(store).reduce((a, r) => a + (r[activeMetric][t] || 0), 0); grand += typeTotals[t]; });
        const sum = (m) => Object.values(store).reduce((a, r) => a + types.reduce((s, t) => s + (r[m][t] || 0), 0), 0);
        const kpis = { groups: Object.keys(store).length, pieces: sum("pieces"), meters: sum("meters"), revenue: sum("revenue") };
        return { typeTotals, grand, kpis };
    }, [data, category, types, activeMetric]);

    // Har category ka pieces total (toggle buttons pe dikhane ke liye).
    const catPieces = (catId) => {
        const store = data[catId] || {};
        const t = SALE_CATEGORIES.find((c) => c.id === catId).types;
        return Object.values(store).reduce((a, r) => a + t.reduce((s, ty) => s + (r.pieces[ty] || 0), 0), 0);
    };

    if (!bookings.length) return <div className="campus-orders-empty">No orders yet.</div>;

    const maxTotal = rows.length ? rows[0].total : 1;

    return (
        <div className="campus-panel campus-sales">
            <div className="campus-seg campus-seg--cats">
                {SALE_CATEGORIES.map((c) => (
                    <button key={c.id} type="button" className={category === c.id ? "is-active" : ""} onClick={() => setCategory(c.id)}>
                        <span className="campus-cat-label">{c.label}</span>
                        <span className="campus-cat-total">{catPieces(c.id)} pcs</span>
                    </button>
                ))}
            </div>

            <div className="campus-stat-row">
                <div className="campus-stat campus-stat--accent"><span className="campus-stat-label">{cat.unit === "Colour" ? "Tie colours" : `${cat.label} fabrics`}</span><span className="campus-stat-value">{kpis.groups}</span></div>
                <div className="campus-stat"><span className="campus-stat-label">Pieces</span><span className="campus-stat-value">{Math.round(kpis.pieces)}</span></div>
                {category !== "tie" && <div className="campus-stat"><span className="campus-stat-label">Fabric used</span><span className="campus-stat-value">{fmtM(kpis.meters)}</span></div>}
                <div className="campus-stat campus-stat--good"><span className="campus-stat-label">Revenue</span><span className="campus-stat-value">{fmtINR(kpis.revenue)}</span></div>
            </div>

            <div className="campus-sales-controls">
                <div className="campus-seg">
                    {metrics.map((m) => (
                        <button key={m.id} type="button" className={activeMetric === m.id ? "is-active" : ""} onClick={() => setMetric(m.id)}>{m.label}</button>
                    ))}
                </div>
                <div className="campus-seg campus-seg--right">
                    <button type="button" className={view === "chart" ? "is-active" : ""} onClick={() => setView("chart")}>Chart</button>
                    <button type="button" className={view === "table" ? "is-active" : ""} onClick={() => setView("table")}>Table</button>
                </div>
            </div>

            {types.length > 1 && (
                <div className="campus-legend">
                    {types.map((t) => (
                        <span className="campus-legend-item" key={t}>
                            <span className="campus-legend-dot" style={{ background: TYPE_COLOR[t] }} />
                            {GARMENT_LABEL[t]}
                        </span>
                    ))}
                </div>
            )}

            {!rows.length ? (
                <div className="campus-orders-empty">No {cat.label.toLowerCase()} sales data yet.</div>
            ) : view === "chart" ? (
                <div className="campus-sales-chart" onMouseLeave={() => setHover(null)}>
                    {rows.map((r) => (
                        <div className="campus-bar-row" key={r.name}>
                            <div className="campus-bar-label" title={r.name}>{r.name}</div>
                            <div className="campus-bar-track">
                                <div className="campus-bar" style={{ width: `${Math.max(2, (r.total / maxTotal) * 100)}%` }}>
                                    {types.map((t) => {
                                        const v = r.all[activeMetric][t] || 0;
                                        if (v <= 0) return null;
                                        return (
                                            <div
                                                key={t}
                                                className="campus-bar-seg"
                                                style={{ flexGrow: v, background: TYPE_COLOR[t] }}
                                                onMouseMove={(e) => setHover({ x: e.clientX, y: e.clientY, fabric: r.name, type: t, v })}
                                                onMouseEnter={(e) => setHover({ x: e.clientX, y: e.clientY, fabric: r.name, type: t, v })}
                                            />
                                        );
                                    })}
                                </div>
                                <span className="campus-bar-total">{fmtVal(r.total)}</span>
                            </div>
                        </div>
                    ))}
                    {hover && (
                        <div className="campus-tooltip" style={{ left: hover.x + 14, top: hover.y + 14 }}>
                            <div className="campus-tooltip-title">{hover.fabric}</div>
                            <div className="campus-tooltip-row">
                                <span className="campus-legend-dot" style={{ background: TYPE_COLOR[hover.type] }} />
                                {GARMENT_LABEL[hover.type]}<strong>{fmtVal(hover.v)}</strong>
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <div className="campus-table-wrap">
                    <table className="campus-table">
                        <thead>
                            <tr>
                                <th>{cat.unit}</th>
                                {types.map((t) => <th key={t} className="r">{GARMENT_LABEL[t]}</th>)}
                                <th className="r">Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.name}>
                                    <td><strong>{r.name}</strong></td>
                                    {types.map((t) => <td key={t} className="r">{r.all[activeMetric][t] ? fmtVal(r.all[activeMetric][t]) : "—"}</td>)}
                                    <td className="r"><strong>{fmtVal(r.total)}</strong></td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td><strong>Total</strong></td>
                                {types.map((t) => <td key={t} className="r"><strong>{fmtVal(typeTotals[t])}</strong></td>)}
                                <td className="r"><strong>{fmtVal(grand)}</strong></td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}
            <p className="campus-note">Suiting = coat/pant/vest · Shirting = shirt · Ties are grouped by colour. Shades are ignored. Revenue is split across garments by weight (approximate).</p>
        </div>
    );
}

/* ───────────────────────── Orders tab ───────────────────────── */
function OrdersTab({ bookings, loading }) {
    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [campusFilter, setCampusFilter] = useState("");
    const [selected, setSelected] = useState(null);
    const [view, setView] = useState("active"); // active | history

    // Delivered / cancelled = kaam khatam → History me. "Missing" abhi active
    // rehta hai kyunki uske baaki items dene baaki hain.
    const isDone = useCallback((b) => {
        const st = effectiveStatus(b).status;
        return st === "delivered" || st === "cancelled";
    }, []);

    const activeCount = useMemo(() => bookings.filter((b) => !isDone(b)).length, [bookings, isDone]);
    const historyCount = bookings.length - activeCount;

    const campusList = useMemo(() => {
        const s = new Set();
        bookings.forEach((b) => b.campus && s.add(b.campus));
        return Array.from(s).sort();
    }, [bookings]);

    const filtered = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        return bookings.filter((b) => {
            if (view === "active" ? isDone(b) : !isDone(b)) return false;
            if (statusFilter && !matchesStatus(b, statusFilter)) return false;
            if (campusFilter && b.campus !== campusFilter) return false;
            if (!q) return true;
            return [b.invoice_no, b.customer_name, b.customer_mobile, b.campus, b.otp, b.created_by_name]
                .filter(Boolean).map((x) => String(x).toLowerCase()).some((p) => p.includes(q));
        });
    }, [bookings, searchQuery, statusFilter, campusFilter, view, isDone]);

    const hasFilters = !!searchQuery.trim() || !!statusFilter || !!campusFilter;

    const stickerFilters = useMemo(
        () => ({ campus: campusFilter, status: statusFilter }),
        [campusFilter, statusFilter]
    );
    const stickerCount = useMemo(
        () => filterBookingsForStickers(bookings, stickerFilters).length,
        [bookings, stickerFilters]
    );
    const { colors: stickerColors, setColor: setStickerColor, resetColors: resetStickerColors } = useStickerColors();

    // Interview cards — jo orders sticker filter me aate hain unhi ka (same
    // campus/status filter). driveBookings pehle se filtered hain.
    const [cardBusy, setCardBusy] = useState("");
    const cardList = useMemo(
        () => filterBookingsForStickers(bookings, stickerFilters),
        [bookings, stickerFilters]
    );
    const makeCards = async () => {
        if (cardBusy) return;
        try {
            await generateInterviewCardsPDF(cardList, (m) => setCardBusy(m || "…"));
        } catch (e) {
            console.log("interview cards", e);
            alert("Could not build the cards PDF. Please try again.");
        } finally {
            setCardBusy("");
        }
    };

    return (
        <>
            <div className="campus-seg campus-orders-view-seg">
                <button type="button" className={view === "active" ? "is-active" : ""} onClick={() => setView("active")}>
                    Active<em className="campus-seg-n">{activeCount}</em>
                </button>
                <button type="button" className={view === "history" ? "is-active" : ""} onClick={() => setView("history")}>
                    History<em className="campus-seg-n">{historyCount}</em>
                </button>
            </div>

            <div className="campus-orders-toolbar">
                <div className="campus-orders-search">
                    <span className="campus-orders-search-icon" aria-hidden="true">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                            <path d="M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15Z" stroke="currentColor" strokeWidth="2" />
                            <path d="M16.5 16.5 21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                    </span>
                    <input type="search" placeholder="Search invoice, name, mobile, campus, OTP…" value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)} autoComplete="off" />
                </div>
                <select className="campus-orders-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    <option value="">All statuses</option>
                    {STICKER_STATUS_OPTIONS.map((s) => (
                        <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                    ))}
                </select>
                <select className="campus-orders-select" value={campusFilter} onChange={(e) => setCampusFilter(e.target.value)}>
                    <option value="">All campuses</option>
                    {campusList.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <button
                    type="button"
                    className="campus-bulk-btn"
                    style={{ padding: "8px 14px", fontSize: "12px" }}
                    disabled={loading || !stickerCount}
                    onClick={() => generateStickerSheetPDF(bookings, stickerFilters, stickerColors)}
                    title="12×18 PDF download — campus + status filters (not search)"
                >
                    Sticker PDF ({stickerCount})
                </button>
                <button
                    type="button"
                    className="campus-bulk-btn"
                    style={{ padding: "8px 14px", fontSize: "12px" }}
                    disabled={loading || !!cardBusy || !cardList.length}
                    onClick={makeCards}
                    title="Interview companion cards PDF — front (name + quote) + back (brand + QR). Campus/status filters apply."
                >
                    {cardBusy ? cardBusy : `Interview Cards (${cardList.length})`}
                </button>
            </div>

            <StickerColorPickers
                colors={stickerColors}
                setColor={setStickerColor}
                resetColors={resetStickerColors}
                className="campus-sticker-colors"
            />

            <div className="campus-orders-meta">
                <span className="campus-orders-count">{filtered.length}</span>
                <span className="campus-orders-meta-text">shown · {bookings.length} total campus orders</span>
                {hasFilters && (
                    <button type="button" className="campus-orders-clear"
                        onClick={() => { setSearchQuery(""); setStatusFilter(""); setCampusFilter(""); }}>Clear all</button>
                )}
            </div>

            {loading && !bookings.length ? (
                <div className="campus-orders-empty">Loading campus orders…</div>
            ) : !filtered.length ? (
                <div className="campus-orders-empty">No campus orders found.</div>
            ) : (
                <div className="campus-orders-list">
                    {filtered.map((b) => {
                        const total = num(b.total_amount);
                        const remaining = dueOf(b);
                        const lines = garmentLines(b);
                        const eff = effectiveStatus(b);
                        return (
                            <div key={b.id || b.invoice_no} className={`campus-order-card${b.urgent ? " is-urgent" : ""}`} onClick={() => setSelected(b)}>
                                <div className="campus-order-card-top">
                                    <div className="campus-order-head">
                                        {b.urgent && (
                                            <span className="campus-urgent-tag"><span className="campus-urgent-dot" />Urgent</span>
                                        )}
                                        <div className="campus-order-invoice">{b.invoice_no || "—"}</div>
                                        <div className="campus-order-name">{b.customer_name || "Unnamed"}</div>
                                    </div>
                                    <span className={`campus-status campus-status--${eff.status}`}>{eff.status}</span>
                                </div>

                                <div className="campus-order-meta">
                                    <div><span className="k">Campus</span><span className="v">{b.campus || "—"}</span></div>
                                    <div><span className="k">Ordered</span><span className="v">{fmtDate(b.order_date)}</span></div>
                                    <div><span className="k">Deliver</span><span className="v">{fmtDate(b.delivery_date)}</span></div>
                                </div>

                                {lines.length > 0 && (
                                    <div className="campus-order-items">
                                        {lines.map((l) => (
                                            <div key={l.id} className="campus-order-item">
                                                <span className="campus-order-ic"><img src={l.icon} alt="" /></span>
                                                <span className="campus-order-mid">
                                                    <span className="campus-order-gname">{l.name}</span>
                                                    {l.logo
                                                        ? <img className="campus-order-blogo" src={l.logo} alt={l.brand} />
                                                        : (l.brand && <span className="campus-order-bnone">{l.brand}</span>)}
                                                </span>
                                                <span className="campus-order-fabval">
                                                    {l.fabric || "—"}
                                                    {l.shade && (
                                                        <span className="campus-order-shade">
                                                            <i style={{ background: shadeColor(l.shade) }} />{l.shade}
                                                        </span>
                                                    )}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {eff.missing.length > 0 && (
                                    <div className="campus-order-missing">Missing: {eff.missing.join(", ")}</div>
                                )}

                                <div className="campus-order-card-foot">
                                    <div className="campus-order-total"><small>Total</small><span>{fmtINR(total)}</span></div>
                                    {remaining > 0 && (
                                        <div className="campus-order-balance"><small>Balance</small><b>{fmtINR(remaining)}</b></div>
                                    )}
                                    <button type="button" className="campus-order-invoice-btn"
                                        onClick={(e) => { e.stopPropagation(); generateInvoicePDF(b); }}>Invoice</button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {selected && <CampusOrderDetail booking={selected} onClose={() => setSelected(null)} />}
        </>
    );
}

function CampusOrderDetail({ booking: lightBooking, onClose }) {
    // List light hoti hai (photos/receipts skip) — modal khulte hi poora doc
    // lazily le aao taaki photos, receipt, height/weight sab dikhein.
    const [full, setFull] = useState(null);
    const [photosLoading, setPhotosLoading] = useState(false);
    useEffect(() => {
        let alive = true;
        setFull(null);
        // Photos pehle se hain (SDK fallback se aayi booking) to dobara mat lao
        if (!lightBooking?.id || lightBooking.front_photo || lightBooking.advance_receipt) return undefined;
        setPhotosLoading(true);
        fetchBookingFull(lightBooking.id)
            .then((doc) => { if (alive && doc) setFull(doc); })
            .catch((e) => console.log("booking full fetch", e))
            .finally(() => { if (alive) setPhotosLoading(false); });
        return () => { alive = false; };
    }, [lightBooking]);
    const booking = full || lightBooking;

    // Backend bade photos offload karta hai ("media:<id>" pointer) — Firestore
    // direct read me pointer milta hai, <img> ke liye URL banao.
    const mediaSrc = (v) => (typeof v === "string" && v.startsWith("media:"))
        ? `${CAMPUS_API_BASE}/booking-media/${v.slice(6)}`
        : v;

    const total = num(booking.total_amount);
    const advance = num(booking.advance_amount);
    const remaining = dueOf(booking);
    const balancePaid = num(booking.balance_paid);
    const items = booking.active_items || [];
    const eff = effectiveStatus(booking);
    const deliveredIds = booking.delivered_item_ids || [];
    const measurements = booking.measurements || {};
    const shownMeasures = measurementFields.filter((f) => measurements[f.key]);

    return (
        <div className="campus-detail-overlay" onClick={onClose}>
            <div className="campus-detail-modal" onClick={(e) => e.stopPropagation()}>
                <div className="campus-detail-header">
                    <div>
                        <div className="campus-detail-kicker">{booking.invoice_no || "Campus order"}</div>
                        {booking.urgent && (
                            <span className="campus-urgent-tag"><span className="campus-urgent-dot" />Urgent</span>
                        )}
                        <h3>{booking.customer_name || "Unnamed"}</h3>
                        {booking.urgent && booking.urgent_by_name && (
                            <div className="campus-urgent-by">Flagged by {booking.urgent_by_name}</div>
                        )}
                    </div>
                    <div className="campus-detail-header-right">
                        <span className={`campus-status campus-status--${eff.status}`}>{eff.status}</span>
                        <button type="button" className="campus-detail-invoice" onClick={() => generateInvoicePDF(booking)}>📄 Invoice</button>
                        <button type="button" className="campus-detail-close" onClick={onClose} aria-label="Close">&times;</button>
                    </div>
                </div>

                <div className="campus-detail-body">
                    {photosLoading && !booking.front_photo && (
                        <div className="campus-detail-photos-loading">Loading photos…</div>
                    )}
                    {(booking.front_photo || booking.side_photo) && (
                        <div className="campus-detail-photos">
                            {booking.front_photo && <figure><img src={mediaSrc(booking.front_photo)} alt="Front" /><figcaption>Front</figcaption></figure>}
                            {booking.side_photo && <figure><img src={mediaSrc(booking.side_photo)} alt="Side" /><figcaption>Side</figcaption></figure>}
                        </div>
                    )}
                    {booking.customer_mobile && (
                        <a className="campus-detail-call" href={`tel:${booking.customer_mobile}`}>📞 Call {booking.customer_mobile}</a>
                    )}

                    <DetailSection title="Customer">
                        <DetailField label="Gender" value={booking.gender} cap />
                        <DetailField label="Height" value={booking.height_ft ? `${booking.height_ft} (${booking.height_cm} cm)` : booking.height_cm} />
                        <DetailField label="Age" value={booking.age_years} />
                        <DetailField label="Weight" value={booking.weight_kg ? `${booking.weight_kg} kg` : ""} />
                        <DetailField label="Campus" value={booking.campus} />
                    </DetailSection>

                    <DetailSection title="Order">
                        <DetailField label="Order date" value={fmtDate(booking.order_date)} />
                        <DetailField label="Delivery date" value={fmtDate(booking.delivery_date)} />
                        <DetailField label="Booked by" value={booking.created_by_name} force />
                        <DetailField label="Created" value={fmtDateTime(booking.created_at)} />
                    </DetailSection>

                    {(booking.total_amount || booking.advance_amount) && (
                        <DetailSection title="Payment">
                            <DetailField label="Total" value={fmtINR(total)} />
                            <DetailField label="Advance" value={fmtINR(advance)} />
                            {balancePaid > 0 && <DetailField label="Balance paid (at delivery)" value={fmtINR(balancePaid)} />}
                            {remaining > 0 && <DetailField label="Remaining" value={fmtINR(remaining)} highlight />}
                            {booking.payment_mode && <DetailField label="Mode" value={booking.payment_mode === "both" ? "Cash + Online" : booking.payment_mode} cap />}
                            {booking.payment_mode === "both" && <DetailField label="Cash" value={fmtINR(booking.advance_cash)} />}
                            {booking.payment_mode === "both" && <DetailField label="Online" value={fmtINR(booking.advance_online)} />}
                        </DetailSection>
                    )}

                    {items.length > 0 && (
                        <div className="campus-detail-section">
                            <div className="campus-detail-section-title">Items</div>
                            {items.map((item) => {
                                const isTie = item.type === "tie";
                                const fab = booking[`${item.id}_fabric`] || "";
                                const shd = booking[`${item.id}_shade`] || "";
                                const col = booking[`${item.id}_color`] || "";
                                const det = booking[`${item.id}_detail`] || "";
                                const img = booking[`${item.id}_ref_image`] || null;
                                const desc = isTie ? (col || "—") : fab ? `${fab}${shd ? ` · Shade ${shd}` : ""}${det ? ` · ${det}` : ""}` : "—";
                                return (
                                    <div key={item.id} className="campus-item-row">
                                        <div>
                                            <strong>{item.name}</strong>
                                            {deliveredIds.length > 0 && (
                                                deliveredIds.includes(item.id)
                                                    ? <span className="campus-item-flag is-done"> ✓ delivered</span>
                                                    : <span className="campus-item-flag is-missing"> missing</span>
                                            )}
                                            <span className="campus-item-desc">{desc}</span>
                                        </div>
                                        {!isTie && img && <img src={img} alt="ref" className="campus-item-thumb" />}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {shownMeasures.length > 0 && (
                        <div className="campus-detail-section">
                            <div className="campus-detail-section-title">Measurements (inches)</div>
                            <div className="campus-detail-grid">
                                {shownMeasures.map((f) => (
                                    <div key={f.key} className="campus-detail-cell"><span>{f.label}</span><strong>{measurements[f.key]}"</strong></div>
                                ))}
                            </div>
                        </div>
                    )}

                    {booking.otp && (
                        <div className="campus-detail-otp">
                            <span>Customer OTP</span><strong>{booking.otp}</strong><small>Required at delivery</small>
                        </div>
                    )}
                    {booking.advance_receipt && (
                        <div className="campus-detail-section">
                            <div className="campus-detail-section-title">Advance receipt</div>
                            <img src={mediaSrc(booking.advance_receipt)} alt="Receipt" className="campus-detail-receipt" />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function DetailSection({ title, children }) {
    return (
        <div className="campus-detail-section">
            <div className="campus-detail-section-title">{title}</div>
            <div className="campus-detail-grid">{children}</div>
        </div>
    );
}
function DetailField({ label, value, cap, highlight, force }) {
    if ((value === undefined || value === null || value === "" || value === "—") && !force) return null;
    return (
        <div className="campus-detail-cell">
            <span>{label}</span>
            <strong className={`${cap ? "campus-cap" : ""} ${highlight ? "campus-due" : ""}`.trim()}>{value || "—"}</strong>
        </div>
    );
}

/* ───────────────────────── Production tab ─────────────────────────
   Kis fabric me kitne coat/pant/etc banne hain (#1). */
function ProductionTab({ garments, consumption }) {
    const { rows, typeTotals, totalPieces, totalMeters } = useMemo(() => {
        const map = {}; // fabricKey -> { name, coat, pant, ... , meters }
        const typeTotals = { coat: 0, pant: 0, shirt: 0, vest_coat: 0, tie: 0 };
        let totalPieces = 0, totalMeters = 0;
        garments.forEach((g) => {
            const name = g.fabric || (g.itemType === "tie" ? "(Tie)" : "(No fabric)");
            const shade = g.itemType === "tie" ? "" : (g.shade || "");
            const k = `${fabricKey(name) || "—"}||${fabricKey(shade)}`;   // fabric+shade wise
            if (!map[k]) map[k] = { name, shade, coat: 0, pant: 0, shirt: 0, vest_coat: 0, tie: 0, meters: 0 };
            if (map[k][g.itemType] != null) map[k][g.itemType] += 1;
            if (typeTotals[g.itemType] != null) typeTotals[g.itemType] += 1;
            const m = consumption[g.itemType] || 0;
            map[k].meters += m;
            totalMeters += m;
            totalPieces += 1;
        });
        const rows = Object.values(map).sort((a, b) => b.meters - a.meters);
        return { rows, typeTotals, totalPieces, totalMeters };
    }, [garments, consumption]);

    if (!garments.length) return <div className="campus-orders-empty">No garments to produce yet.</div>;

    return (
        <div className="campus-panel">
            <div className="campus-stat-row">
                {GARMENT_TYPES.map((t) => (
                    <div className="campus-stat" key={t}>
                        <span className="campus-stat-label">{GARMENT_LABEL[t]}</span>
                        <span className="campus-stat-value">{typeTotals[t]}</span>
                    </div>
                ))}
                <div className="campus-stat campus-stat--accent">
                    <span className="campus-stat-label">Total pieces</span>
                    <span className="campus-stat-value">{totalPieces}</span>
                </div>
            </div>

            <div className="campus-table-title">Fabric-wise production plan</div>
            <div className="campus-table-wrap">
                <table className="campus-table">
                    <thead>
                        <tr>
                            <th>Fabric</th>
                            <th className="c">Shade</th>
                            {GARMENT_TYPES.map((t) => <th key={t} className="c">{GARMENT_LABEL[t]}</th>)}
                            <th className="c">Pieces</th>
                            <th className="r">Fabric needed</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => {
                            const pieces = GARMENT_TYPES.reduce((a, t) => a + r[t], 0);
                            return (
                                <tr key={`${r.name}||${r.shade}`}>
                                    <td><strong>{r.name}</strong></td>
                                    <td className="c">{r.shade ? <span className="campus-shade-pill">{r.shade}</span> : "—"}</td>
                                    {GARMENT_TYPES.map((t) => <td key={t} className="c">{r[t] || "—"}</td>)}
                                    <td className="c">{pieces}</td>
                                    <td className="r">{fmtM(r.meters)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td><strong>Total</strong></td>
                            <td className="c"></td>
                            {GARMENT_TYPES.map((t) => <td key={t} className="c"><strong>{typeTotals[t]}</strong></td>)}
                            <td className="c"><strong>{totalPieces}</strong></td>
                            <td className="r"><strong>{fmtM(totalMeters)}</strong></td>
                        </tr>
                    </tfoot>
                </table>
            </div>
            <p className="campus-note">Fabric needed = pieces × consumption rate (rates can be edited in the Inventory tab). Ties are counted in pieces, not meters.</p>
        </div>
    );
}

/* ───────────────────────── Inventory tab ─────────────────────────
   Fabric/cover/tie: required vs purchased vs stock (#2-7). */
function InventoryTab({ garments, bookings, inventory, consumption, setConsumption, fabricIssues = [], assignments = {}, fabricOrder = { shared: {}, lastShareAt: 0 }, scopeLabel = "All stalls" }) {
    const [form, setForm] = useState({ category: "fabric", name: "", shade: "", qty: "", cost: "", note: "" });
    const [saving, setSaving] = useState(false);
    const [sharing, setSharing] = useState(false);
    const [orderDealer, setOrderDealer] = useState("");

    // fabric ko fabric+shade se group karte hain (shade-wise stock).
    const fabKey = (name, shade) => `${fabricKey(name) || "—"}||${fabricKey(shade)}`;

    const bookingsById = useMemo(() => {
        const m = {};
        (bookings || []).forEach((b) => { if (b.id) m[b.id] = b; });
        return m;
    }, [bookings]);

    const fabricOrderView = useMemo(() => buildFabricOrder({
        garments,
        bookingsById,
        assignments,
        shared: fabricOrder.shared || {},
    }), [garments, bookingsById, assignments, fabricOrder]);

    const selectedPack = fabricOrderView.packs.find((p) => p.name === orderDealer) || null;
    const canReset = garments.some((g) => fabricOrder.shared?.[g.key]);

    const shareFabricOrder = async () => {
        if (!selectedPack?.keys.length || sharing) return;
        const now = Date.now();
        const when = new Date(now).toLocaleString("en-IN", {
            day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
        });
        const saved = downloadFabricOrderPDFs({ packs: [selectedPack] }, { when, scope: scopeLabel });
        if (!saved) return;
        setSharing(true);
        try {
            const shared = { ...(fabricOrder.shared || {}) };
            selectedPack.keys.forEach((k) => { shared[k] = now; });
            await db.collection(CONFIG_DOC).doc("fabricOrder").set({ shared, lastShareAt: now }, { merge: true });
        } catch (e) {
            console.log("fabric order share", e);
            alert("The PDF downloaded, but the share time could not be saved. Share again before the next order so these pieces are not repeated.");
        } finally {
            setSharing(false);
        }
    };

    const resetFabricOrder = async () => {
        if (!canReset || sharing) return;
        if (!window.confirm("Clear the share memory for this list? The next PDF includes every piece that is still not given to a tailor.")) return;
        const shared = { ...(fabricOrder.shared || {}) };
        garments.forEach((g) => { delete shared[g.key]; });
        const times = Object.values(shared).map(Number).filter((n) => n > 0);
        try {
            await db.collection(CONFIG_DOC).doc("fabricOrder").set({
                shared,
                lastShareAt: times.length ? Math.max(...times) : 0,
            }, { merge: true });
        } catch (e) {
            console.log("fabric order reset", e);
            alert("Could not reset. Please try again.");
        }
    };

    // Har fabric+shade ke garments ko TYPE-wise group karo. Coat ka tailor alag,
    // pant ka alag, shirt ka alag — isliye har type ki sheet alag banti hai.
    // rowKey -> { itemType -> [ { booking, item } ] }
    const piecesByRowType = useMemo(() => {
        const m = {};
        garments.forEach((g) => {
            if (g.itemType === "tie") return;
            const booking = bookingsById[g.bookingId];
            if (!booking) return;
            const item = (booking.active_items || []).find((i) => i.id === g.itemId);
            if (!item) return;
            const key = fabKey(g.fabric || "(No fabric)", g.shade || "");
            if (!m[key]) m[key] = {};
            if (!m[key][g.itemType]) m[key][g.itemType] = [];
            m[key][g.itemType].push({ booking, item });
        });
        return m;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [garments, bookingsById]);

    // required (orders se)
    const required = useMemo(() => {
        const fabric = {}; // key -> { name, shade, meters }
        let covers = 0, ties = 0;
        garments.forEach((g) => {
            if (g.itemType === "tie") { ties += 1; return; }
            if (g.itemType === "coat") covers += 1; // 1 cover per coat
            const name = g.fabric || "(No fabric)";
            const shade = g.shade || "";
            const k = fabKey(name, shade);
            if (!fabric[k]) fabric[k] = { name, shade, meters: 0 };
            fabric[k].meters += consumption[g.itemType] || 0;
        });
        return { fabric, covers, ties };
    }, [garments, consumption]);

    // purchased (Firestore se)
    const purchased = useMemo(() => {
        const fabric = {}; let covers = 0, ties = 0, fabricCost = 0, coverCost = 0, tieCost = 0;
        inventory.forEach((it) => {
            const qty = num(it.qty), cost = num(it.cost);
            if (it.category === "fabric") {
                const k = fabKey(it.name, it.shade);
                if (!fabric[k]) fabric[k] = { name: it.name || "—", shade: it.shade || "", qty: 0, cost: 0 };
                fabric[k].qty += qty; fabric[k].cost += cost; fabricCost += cost;
            } else if (it.category === "cover") { covers += qty; coverCost += cost; }
            else if (it.category === "tie") { ties += qty; tieCost += cost; }
        });
        return { fabric, covers, ties, fabricCost, coverCost, tieCost };
    }, [inventory]);

    // Tailor ko diya gaya kapda, fabric+shade wise (kapde ka hisab)
    const issuedByFab = useMemo(() => {
        const m = {};
        fabricIssues.forEach((f) => {
            const k = fabKey(f.fabric, f.shade);
            m[k] = (m[k] || 0) + num(f.meters);
        });
        return m;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fabricIssues]);

    const fabricRows = useMemo(() => {
        const keys = new Set([...Object.keys(required.fabric), ...Object.keys(purchased.fabric), ...Object.keys(issuedByFab)]);
        return Array.from(keys).map((k) => {
            const src = required.fabric[k] || purchased.fabric[k] || {};
            const need = required.fabric[k]?.meters || 0;
            const buy = purchased.fabric[k]?.qty || 0;
            const issued = issuedByFab[k] || 0;
            const nameFromIssue = !src.name && k !== "—" ? (fabricIssues.find((f) => fabKey(f.fabric, f.shade) === k) || {}) : {};
            return {
                key: k,
                name: src.name || nameFromIssue.fabric || k,
                shade: src.shade || nameFromIssue.shade || "",
                need, buy, issued,
                inHand: buy - issued,
                stock: buy - need,
                cost: purchased.fabric[k]?.cost || 0,
            };
        }).sort((a, b) => b.need - a.need);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [required, purchased, issuedByFab]);

    const fabricNames = useMemo(() => {
        const s = new Set();
        fabricRows.forEach((r) => r.name && s.add(r.name));
        return Array.from(s);
    }, [fabricRows]);

    const addPurchase = async () => {
        if (!form.qty || (form.category === "fabric" && !form.name.trim())) {
            alert("Quantity is required (and a fabric name for fabric purchases).");
            return;
        }
        setSaving(true);
        try {
            const ref = db.collection(COL_INVENTORY).doc();
            await ref.set({
                category: form.category,
                name: form.name.trim(),
                shade: form.category === "fabric" ? form.shade.trim() : "",
                qty: num(form.qty),
                cost: num(form.cost),
                note: form.note.trim(),
                date: new Date().toISOString(),
                createdAt: Date.now(),
            });
            logAction(`Add purchase · ${form.category} ${form.name.trim()} (${num(form.qty)})`, [{ col: COL_INVENTORY, id: ref.id, prev: null }]);
            setForm({ category: "fabric", name: "", shade: "", qty: "", cost: "", note: "" });
        } catch (e) { console.log("add purchase", e); alert("Could not save. Please try again."); }
        finally { setSaving(false); }
    };

    const removePurchase = async (it) => {
        if (!window.confirm("Delete this purchase entry?")) return;
        try {
            await db.collection(COL_INVENTORY).doc(it.id).delete();
            logAction(`Delete purchase · ${it.category} ${it.name || ""} (${num(it.qty)})`, [{ col: COL_INVENTORY, id: it.id, prev: stripId({ ...it }) }]);
        }
        catch (e) { console.log("del purchase", e); }
    };

    const saveConsumption = async (type, val) => {
        const next = { ...consumption, [type]: num(val) };
        setConsumption(next);
        try { await db.collection(CONFIG_DOC).doc("consumption").set(next, { merge: true }); }
        catch (e) { console.log("save consumption", e); }
    };

    return (
        <div className="campus-panel">
            <div className="campus-fabric-order">
                <div className="campus-fabric-order-head">
                    <div>
                        <span className="campus-fabric-order-kicker">Order fabric</span>
                        <strong>{scopeLabel}</strong>
                        <span className="campus-fabric-order-when">
                            {fabricOrder.lastShareAt ? `Last shared ${fmtDateTime(fabricOrder.lastShareAt)}` : "Not shared yet"}
                        </span>
                    </div>
                    <div className="campus-fabric-order-actions">
                        <button type="button" className="campus-fabric-order-reset" onClick={resetFabricOrder} disabled={!canReset || sharing}>
                            Reset
                        </button>
                    </div>
                </div>
                <div className="campus-fabric-order-dealers">
                    {fabricOrderView.packs.map((p) => (
                        <button
                            key={p.name}
                            type="button"
                            className={`campus-fabric-order-dealer ${orderDealer === p.name ? "is-on" : ""}`}
                            onClick={() => setOrderDealer(p.name)}
                        >
                            <strong>{p.name}</strong>
                            <em>{p.pieces} pcs · {p.meters.toFixed(1)} m</em>
                        </button>
                    ))}
                </div>
                <p className="campus-fabric-order-note">
                    Open a dealer to see the order. Share downloads only that dealer.
                    {fabricOrderView.shirts > 0 ? ` ${fabricOrderView.shirts} shirt${fabricOrderView.shirts === 1 ? "" : "s"} waiting — shirt meters are not set.` : ""}
                </p>
                {selectedPack && (
                    <div className="campus-fabric-order-detail">
                        <div className="campus-fabric-order-detail-head">
                            <strong>{selectedPack.name}</strong>
                            <button
                                type="button"
                                className="campus-fabric-order-share"
                                onClick={shareFabricOrder}
                                disabled={!selectedPack.keys.length || sharing}
                            >
                                {sharing ? "Saving…" : "Share PDF"}
                            </button>
                        </div>
                        {!selectedPack.rows.length ? (
                            <p className="campus-fabric-order-note">Nothing new to order for this dealer.</p>
                        ) : (
                            <div className="campus-table-wrap">
                                <table className="campus-table">
                                    <thead>
                                        <tr>
                                            <th>Fabric</th>
                                            <th>Shade</th>
                                            <th className="r">Coat</th>
                                            <th className="r">Pant</th>
                                            <th className="r">Vest</th>
                                            <th className="r">Meters</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedPack.rows.map((r) => (
                                            <tr key={`${r.fabric}|${r.shade}`}>
                                                <td><strong>{r.fabric}</strong></td>
                                                <td>{r.shade || "—"}</td>
                                                <td className="r">{r.coat || "—"}</td>
                                                <td className="r">{r.pant || "—"}</td>
                                                <td className="r">{r.vest || "—"}</td>
                                                <td className="r">{r.meters.toFixed(1)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <td><strong>Total</strong></td>
                                            <td></td>
                                            <td className="r"><strong>{selectedPack.rows.reduce((a, r) => a + r.coat, 0) || "—"}</strong></td>
                                            <td className="r"><strong>{selectedPack.rows.reduce((a, r) => a + r.pant, 0) || "—"}</strong></td>
                                            <td className="r"><strong>{selectedPack.rows.reduce((a, r) => a + r.vest, 0) || "—"}</strong></td>
                                            <td className="r"><strong>{selectedPack.meters.toFixed(1)}</strong></td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        )}
                        {selectedPack.unset.length > 0 && (
                            <div className="campus-fabric-order-unset">
                                <span>Meter not set ({selectedPack.unset.length}) — not in the PDF. 5XL / 6XL coat, waist above 47", or a missing measurement.</span>
                                <ul>
                                    {selectedPack.unset.map((u) => (
                                        <li key={u.key}>
                                            {u.invoiceNo} · {u.itemName} · chest {u.chest ?? "—"} · waist {u.waist ?? "—"} · {u.fabric}{u.shade ? ` · ${u.shade}` : ""}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
            </div>
            <div className="campus-stat-row">
                <div className="campus-stat"><span className="campus-stat-label">Covers needed</span><span className="campus-stat-value">{required.covers}</span></div>
                <div className="campus-stat"><span className="campus-stat-label">Covers bought</span><span className="campus-stat-value">{purchased.covers}</span></div>
                <div className={`campus-stat ${purchased.covers - required.covers < 0 ? "campus-stat--bad" : "campus-stat--good"}`}>
                    <span className="campus-stat-label">Covers stock</span><span className="campus-stat-value">{purchased.covers - required.covers}</span>
                </div>
                <div className="campus-stat"><span className="campus-stat-label">Ties needed</span><span className="campus-stat-value">{required.ties}</span></div>
                <div className="campus-stat"><span className="campus-stat-label">Ties bought</span><span className="campus-stat-value">{purchased.ties}</span></div>
                <div className={`campus-stat ${purchased.ties - required.ties < 0 ? "campus-stat--bad" : "campus-stat--good"}`}>
                    <span className="campus-stat-label">Ties stock</span><span className="campus-stat-value">{purchased.ties - required.ties}</span>
                </div>
            </div>

            <div className="campus-table-title">Add purchase</div>
            <div className="campus-purchase-form">
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                    <option value="fabric">Fabric (meters)</option>
                    <option value="cover">Cover (pcs)</option>
                    <option value="tie">Tie (pcs)</option>
                </select>
                {form.category === "fabric" && (
                    <input list="campus-fabric-names" placeholder="Fabric name" value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })} />
                )}
                {form.category === "fabric" && (
                    <input list="campus-fabric-shades" placeholder="Shade" value={form.shade} style={{ flex: "0 1 100px" }}
                        onChange={(e) => setForm({ ...form, shade: e.target.value })} />
                )}
                <datalist id="campus-fabric-names">
                    {fabricNames.map((n) => <option key={n} value={n} />)}
                </datalist>
                <datalist id="campus-fabric-shades">
                    {[...new Set(fabricRows.map((r) => r.shade).filter(Boolean))].map((s) => <option key={s} value={s} />)}
                </datalist>
                <input type="number" placeholder={form.category === "fabric" ? "Meters" : "Pieces"} value={form.qty}
                    onChange={(e) => setForm({ ...form, qty: e.target.value })} />
                <input type="number" placeholder="Cost ₹" value={form.cost}
                    onChange={(e) => setForm({ ...form, cost: e.target.value })} />
                <input placeholder="Note (optional)" value={form.note}
                    onChange={(e) => setForm({ ...form, note: e.target.value })} />
                <button type="button" onClick={addPurchase} disabled={saving}>{saving ? "Saving…" : "+ Add"}</button>
            </div>

            <div className="campus-table-title">Fabric stock</div>
            <div className="campus-table-wrap">
                <table className="campus-table">
                    <thead><tr><th>Fabric</th><th className="c">Shade</th><th className="r">Needed</th><th className="r">Purchased</th><th className="r">Issued to tailors</th><th className="c">In hand</th><th className="r">Spent</th><th className="c">Tailor</th></tr></thead>
                    <tbody>
                        {fabricRows.length ? fabricRows.map((r) => (
                            <tr key={r.key}>
                                <td><strong>{r.name}</strong></td>
                                <td className="c">{r.shade ? <span className="campus-shade-pill">{r.shade}</span> : "—"}</td>
                                <td className="r">{fmtM(r.need)}</td>
                                <td className="r">{fmtM(r.buy)}</td>
                                <td className="r">{r.issued ? <strong>{fmtM(r.issued)}</strong> : "—"}</td>
                                <td className="c">
                                    {(() => {
                                        const remainingNeed = Math.max(0, r.need - r.issued);
                                        const short = r.inHand < remainingNeed;
                                        return (
                                            <span className={`campus-stock ${short ? "campus-stock--neg" : "campus-stock--ok"}`}>
                                                {fmtM(r.inHand)}{short ? ` (short ${fmtM(remainingNeed - r.inHand)})` : ""}
                                            </span>
                                        );
                                    })()}
                                </td>
                                <td className="r">{fmtINR(r.cost)}</td>
                                <td className="c">
                                    {(() => {
                                        const byType = piecesByRowType[r.key] || {};
                                        const btns = GARMENT_TYPES.filter((t) => t !== "tie" && byType[t]?.length);
                                        if (!btns.length) return "—";
                                        return (
                                            <div className="campus-sheet-btns">
                                                {btns.map((t) => (
                                                    <button key={t} type="button" className="campus-sheet-btn"
                                                        title={`${GARMENT_LABEL[t]} tailor sheet (PDF)`}
                                                        onClick={() => generateFabricTailorSheet(r.name, r.shade, t, byType[t])}>
                                                        📄 {GARMENT_LABEL[t]} ({byType[t].length})
                                                    </button>
                                                ))}
                                            </div>
                                        );
                                    })()}
                                </td>
                            </tr>
                        )) : <tr><td colSpan={8} className="c">No fabric data yet.</td></tr>}
                    </tbody>
                </table>
            </div>

            <div className="campus-table-title">Purchase log</div>
            <div className="campus-table-wrap">
                <table className="campus-table">
                    <thead><tr><th>Date</th><th>Type</th><th>Name</th><th className="c">Shade</th><th className="r">Qty</th><th className="r">Cost</th><th>Note</th><th></th></tr></thead>
                    <tbody>
                        {inventory.length ? [...inventory].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)).map((it) => (
                            <tr key={it.id}>
                                <td>{fmtDate(it.date)}</td>
                                <td className="campus-cap">{it.category}</td>
                                <td>{it.name || "—"}</td>
                                <td className="c">{it.shade ? <span className="campus-shade-pill">{it.shade}</span> : "—"}</td>
                                <td className="r">{num(it.qty)}{it.category === "fabric" ? " m" : " pcs"}</td>
                                <td className="r">{fmtINR(it.cost)}</td>
                                <td>{it.note || "—"}</td>
                                <td className="c"><button type="button" className="campus-del" onClick={() => removePurchase(it)}>✕</button></td>
                            </tr>
                        )) : <tr><td colSpan={8} className="c">No purchases logged.</td></tr>}
                    </tbody>
                </table>
            </div>

            <div className="campus-table-title">Consumption rates (meters per piece)</div>
            <div className="campus-consumption">
                {["coat", "pant", "shirt", "vest_coat"].map((t) => (
                    <label key={t} className="campus-consumption-item">
                        <span>{GARMENT_LABEL[t]}</span>
                        <input type="number" step="0.1" value={consumption[t] ?? ""} onChange={(e) => saveConsumption(t, e.target.value)} />
                    </label>
                ))}
            </div>
        </div>
    );
}

/* ───────────────────────── Payments tab ─────────────────────────
   Kitna aaya / kitna milna / kitna kharch (#8,9,10). */
function PaymentsTab({ bookings, allBookings = [], inventory, expenses, drive }) {
    const [form, setForm] = useState({ label: "", amount: "", category: "general" });
    const [saving, setSaving] = useState(false);

    // ── GST register — poore mahine ka, drive filter se azad (GST business ka
    //    hota hai, ek stall drive ka nahi). ──
    const gstMonths = useMemo(() => monthsWithBills(allBookings), [allBookings]);
    const [gstMonth, setGstMonth] = useState("");
    const activeGstMonth = gstMonth || gstMonths[0] || "";
    const gstPreview = useMemo(
        () => (activeGstMonth ? buildRegister(allBookings, activeGstMonth) : null),
        [allBookings, activeGstMonth]
    );

    const totals = useMemo(() => {
        let orderValue = 0, received = 0, remaining = 0;
        bookings.forEach((b) => {
            const total = num(b.total_amount);
            const adv = paidOf(b);
            orderValue += total; received += adv; remaining += dueOf(b);
        });
        const purchaseCost = inventory.reduce((a, it) => a + num(it.cost), 0);
        const manualExpense = expenses.reduce((a, e) => a + num(e.amount), 0);
        const totalExpense = purchaseCost + manualExpense;
        return { orderValue, received, remaining, purchaseCost, manualExpense, totalExpense, profit: received - totalExpense };
    }, [bookings, inventory, expenses]);

    const addExpense = async () => {
        if (!form.amount || !form.label.trim()) { alert("Label and amount are required."); return; }
        setSaving(true);
        try {
            const ref = db.collection(COL_EXPENSES).doc();
            await ref.set({
                label: form.label.trim(), amount: num(form.amount), category: form.category,
                date: new Date().toISOString(), createdAt: Date.now(),
            });
            logAction(`Add expense · ${form.label.trim()} (${fmtINR(num(form.amount))})`, [{ col: COL_EXPENSES, id: ref.id, prev: null }]);
            setForm({ label: "", amount: "", category: "general" });
        } catch (e) { console.log("add expense", e); alert("Could not save. Please try again."); }
        finally { setSaving(false); }
    };
    const removeExpense = async (exp) => {
        if (!window.confirm("Delete this expense?")) return;
        try {
            await db.collection(COL_EXPENSES).doc(exp.id).delete();
            logAction(`Delete expense · ${exp.label} (${fmtINR(num(exp.amount))})`, [{ col: COL_EXPENSES, id: exp.id, prev: stripId({ ...exp }) }]);
        } catch (e) { console.log("del expense", e); }
    };

    return (
        <div className="campus-panel">
            <CampusHisab bookings={allBookings} drive={drive} />

            <div className="campus-table-title">Order value of this stall’s new bookings</div>
            <div className="campus-stat-row">
                <div className="campus-stat"><span className="campus-stat-label">Order value</span><span className="campus-stat-value">{fmtINR(totals.orderValue)}</span></div>
                <div className="campus-stat campus-stat--good"><span className="campus-stat-label">Received</span><span className="campus-stat-value">{fmtINR(totals.received)}</span></div>
                <div className="campus-stat campus-stat--bad"><span className="campus-stat-label">Remaining to collect</span><span className="campus-stat-value">{fmtINR(totals.remaining)}</span></div>
                <div className="campus-stat"><span className="campus-stat-label">Total expense</span><span className="campus-stat-value">{fmtINR(totals.totalExpense)}</span></div>
                <div className={`campus-stat ${totals.profit < 0 ? "campus-stat--bad" : "campus-stat--accent"}`}>
                    <span className="campus-stat-label">Net (received − expense)</span><span className="campus-stat-value">{fmtINR(totals.profit)}</span>
                </div>
            </div>

            <div className="campus-subnote">
                Expense breakdown — Purchases: <strong>{fmtINR(totals.purchaseCost)}</strong> · Other: <strong>{fmtINR(totals.manualExpense)}</strong>
            </div>

            <div className="campus-table-title">GST register — monthly</div>
            <div className="campus-gst-box">
                <div className="campus-gst-controls">
                    <label className="campus-gst-field">
                        <span>Month</span>
                        <select
                            className="campus-orders-select"
                            value={activeGstMonth}
                            onChange={(e) => setGstMonth(e.target.value)}
                            disabled={!gstMonths.length}
                        >
                            {gstMonths.length
                                ? gstMonths.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)
                                : <option value="">No bills yet</option>}
                        </select>
                    </label>
                    <button
                        type="button"
                        className="campus-bulk-btn"
                        disabled={!gstPreview || !gstPreview.rows.length}
                        onClick={() => generateGstRegisterPDF(allBookings, activeGstMonth)}
                        title="Monthly campus sales register for GST filing — downloads as PDF"
                    >
                        📄 Download GST register
                    </button>
                    <span className="campus-gst-hint">
                        Covers the whole month across all campuses — the stall-drive filter does not apply here.
                    </span>
                </div>

                {gstPreview && gstPreview.rows.length > 0 && (
                    <>
                        <div className="campus-gst-stats">
                            <div><span>Bills</span><strong>{gstPreview.billCount}</strong></div>
                            <div><span>Taxable value</span><strong>{fmtINR(gstPreview.totals.taxable)}</strong></div>
                            <div><span>CGST</span><strong>{fmtINR(gstPreview.totals.cgst)}</strong></div>
                            <div><span>SGST</span><strong>{fmtINR(gstPreview.totals.sgst)}</strong></div>
                            <div><span>IGST</span><strong>{fmtINR(gstPreview.totals.igst)}</strong></div>
                            <div className="campus-gst-stats-total">
                                <span>Invoice value</span><strong>{fmtINR(gstPreview.totals.gross)}</strong>
                            </div>
                        </div>
                        <div className="campus-gst-rates">
                            {gstPreview.rateSummary.map((r) => (
                                <span key={r.rate} className="campus-gst-rate-chip">
                                    <em>{r.rate}%</em> {r.billCount} bills · taxable {fmtINR(r.taxable)} · GST {fmtINR(r.cgst + r.sgst + r.igst)}
                                </span>
                            ))}
                        </div>
                    </>
                )}
            </div>

            <div className="campus-table-title">Add expense</div>
            <div className="campus-purchase-form">
                <input placeholder="What was this expense for?" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
                <input type="number" placeholder="Amount ₹" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                    <option value="general">General</option>
                    <option value="tailoring">Tailoring</option>
                    <option value="transport">Transport</option>
                    <option value="salary">Salary</option>
                    <option value="misc">Misc</option>
                </select>
                <button type="button" onClick={addExpense} disabled={saving}>{saving ? "Saving…" : "+ Add"}</button>
            </div>

            <div className="campus-table-title">Expense log</div>
            <div className="campus-table-wrap">
                <table className="campus-table">
                    <thead><tr><th>Date</th><th>Label</th><th>Category</th><th className="r">Amount</th><th></th></tr></thead>
                    <tbody>
                        {expenses.length ? [...expenses].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)).map((e) => (
                            <tr key={e.id}>
                                <td>{fmtDate(e.date)}</td>
                                <td>{e.label}</td>
                                <td className="campus-cap">{e.category}</td>
                                <td className="r">{fmtINR(e.amount)}</td>
                                <td className="c"><button type="button" className="campus-del" onClick={() => removeExpense(e)}>✕</button></td>
                            </tr>
                        )) : <tr><td colSpan={5} className="c">No expenses logged.</td></tr>}
                    </tbody>
                </table>
            </div>
            <p className="campus-note">Fabric, cover and tie purchases from the Inventory tab are counted as expenses automatically. "Received" includes advance + balance payments.</p>
        </div>
    );
}

/* ───────────────────────── Distribution tab ─────────────────────────
   Per-garment tailor assignment + stage + packing (#11,12). */
function DistributionTab({ garments, bookings = [], assignments, tailors = [], fabricIssues = [], consumption = {} }) {
    const [tailorFilter, setTailorFilter] = useState("");
    const [stageFilter, setStageFilter] = useState("");
    const [search, setSearch] = useState("");
    const [typePopup, setTypePopup] = useState(null); // garment type jiska remaining popup khula hai
    const [ordersPopup, setOrdersPopup] = useState(false); // adhoore (partially distributed) orders ki list
    const [purchasePopup, setPurchasePopup] = useState(null); // distributed pieces ki fabric-purchase list (type)

    const bookingsById = useMemo(() => Object.fromEntries(bookings.map((b) => [b.id, b])), [bookings]);

    // Managed tailor list se active naam (assign dropdown ke liye)
    const activeTailorNames = useMemo(
        () => tailors.filter((t) => t.active !== false && t.name).map((t) => t.name).sort((a, b) => a.localeCompare(b)),
        [tailors]
    );

    // ── Bulk assign (fabric + type wise) ──
    const [bulkFab, setBulkFab] = useState("");     // fabric+shade key
    const [bulkType, setBulkType] = useState("");   // garment type
    const [selected, setSelected] = useState(() => new Set());
    const [bulkTailor, setBulkTailor] = useState("");
    const [bulkMeters, setBulkMeters] = useState(""); // saath me kitna kapda de rahe hain (optional)

    const tailorList = useMemo(() => {
        const s = new Set();
        Object.values(assignments).forEach((a) => a.tailor && s.add(a.tailor));
        return Array.from(s).sort();
    }, [assignments]);

    const bulkFabKey = (g) => `${fabricKey(g.fabric) || "—"}||${fabricKey(g.shade)}`;
    const garmentByKey = useMemo(() => Object.fromEntries(garments.map((g) => [g.key, g])), [garments]);

    // Fabric+shade options (tie chhod ke) — sirf jinme unassigned pieces bache hain
    const fabricOptions = useMemo(() => {
        const m = new Map();
        garments.forEach((g) => {
            if (g.itemType === "tie") return;
            if (assignments[g.key]?.tailor) return; // distribute ho chuka
            const k = bulkFabKey(g);
            if (!m.has(k)) m.set(k, { key: k, name: g.fabric || "(No fabric)", shade: g.shade || "" });
        });
        return [...m.values()].sort((a, b) => a.name.localeCompare(b.name));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [garments, assignments]);

    // Selected fabric ke andar kaun-kaun se type (sirf unassigned count)
    const bulkTypes = useMemo(() => {
        if (!bulkFab) return [];
        const cnt = {};
        garments.forEach((g) => {
            if (g.itemType === "tie" || bulkFabKey(g) !== bulkFab) return;
            if (assignments[g.key]?.tailor) return;
            cnt[g.itemType] = (cnt[g.itemType] || 0) + 1;
        });
        return GARMENT_TYPES.filter((t) => cnt[t]).map((t) => [t, cnt[t]]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [garments, bulkFab, assignments]);

    // Us fabric+type ke garments (checklist) — distribute hue list se hat jaate hain
    const bulkList = useMemo(() => {
        if (!bulkFab || !bulkType) return [];
        return garments.filter((g) =>
            g.itemType !== "tie" && bulkFabKey(g) === bulkFab && g.itemType === bulkType &&
            !assignments[g.key]?.tailor
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [garments, bulkFab, bulkType, assignments]);

    // Selected tailor ke paas is fabric ka kitna kapda pada hai (given - used)
    const tailorFabricLeft = useMemo(() => {
        if (!bulkTailor || !bulkFab) return null;
        const k = (fabric, shade) => `${fabricKey(fabric) || "—"}||${fabricKey(shade)}`;
        let given = 0, used = 0;
        fabricIssues.forEach((f) => {
            if (f.tailor === bulkTailor && k(f.fabric, f.shade) === bulkFab) given += num(f.meters);
        });
        Object.values(assignments).forEach((a) => {
            if (a.tailor !== bulkTailor || a.itemType === "tie") return;
            if (k(a.fabric, a.shade) !== bulkFab) return;
            used += consumption[a.itemType] || 0;
        });
        return given - used;
    }, [bulkTailor, bulkFab, fabricIssues, assignments, consumption]);

    const allChecked = bulkList.length > 0 && bulkList.every((g) => selected.has(g.key));
    const toggleOne = (key) => setSelected((s) => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n; });
    const toggleAll = () => setSelected((s) => {
        const n = new Set(s);
        if (bulkList.every((g) => n.has(g.key))) bulkList.forEach((g) => n.delete(g.key));
        else bulkList.forEach((g) => n.add(g.key));
        return n;
    });

    const assignBulk = async () => {
        const t = bulkTailor.trim();
        const keys = [...selected].filter((k) => garmentByKey[k]);
        if (!t) { alert("Please enter the tailor's name."); return; }
        if (!keys.length) { alert("Select at least one garment."); return; }
        if (String(bulkMeters).trim() === "" || !(num(bulkMeters) >= 0)) { alert("Enter the fabric meters given — enter 0 if the tailor will use fabric already with them."); return; }
        try {
            const undoOps = keys.map((k) => ({ col: COL_ASSIGN, id: k, prev: assignments[k] ? stripId({ ...assignments[k] }) : null }));
            const batch = db.batch();
            keys.forEach((k) => {
                const g = garmentByKey[k];
                const prev = assignments[k] || {};
                batch.set(db.collection(COL_ASSIGN).doc(k), {
                    bookingId: g.bookingId, invoiceNo: g.invoiceNo, itemId: g.itemId,
                    itemName: g.itemName, itemType: g.itemType, fabric: g.fabric, shade: g.shade || "",
                    customerName: g.customerName, campus: g.campus,
                    tailor: t,
                    stage: (prev.stage && prev.stage !== "pending") ? prev.stage : "cutting",
                    updatedAt: Date.now(),
                }, { merge: true });
            });
            // Fabric ledger entry — har assign ke saath zaruri (kapde ka hisab)
            const meters = num(bulkMeters);
            if (meters > 0) {
                const fabOpt = fabricOptions.find((f) => f.key === bulkFab);
                const issueRef = db.collection(COL_FABRIC_ISSUES).doc();
                undoOps.push({ col: COL_FABRIC_ISSUES, id: issueRef.id, prev: null });
                batch.set(issueRef, {
                    tailor: t,
                    fabric: fabOpt?.name || "(No fabric)",
                    shade: fabOpt?.shade || "",
                    meters,
                    pieces: keys.length,
                    note: `Bulk assign · ${GARMENT_LABEL[bulkType] || bulkType} × ${keys.length}`,
                    date: new Date().toISOString(),
                    createdAt: Date.now(),
                });
            }
            await batch.commit();
            logAction(`Bulk assign ${keys.length} pcs → ${t} (${fmtM(meters)})`, undoOps);
            setSelected(new Set());
            setBulkMeters("");
        } catch (e) { console.log("bulk assign", e); alert("Could not assign. Please try again."); }
    };

    const saveAssign = async (g, patch, label) => {
        const prev = assignments[g.key] || {};
        const prevSnap = assignments[g.key] ? stripId({ ...assignments[g.key] }) : null;
        try {
            await db.collection(COL_ASSIGN).doc(g.key).set({
                bookingId: g.bookingId, invoiceNo: g.invoiceNo, itemId: g.itemId,
                itemName: g.itemName, itemType: g.itemType, fabric: g.fabric, shade: g.shade || "",
                customerName: g.customerName, campus: g.campus,
                tailor: prev.tailor || "", stage: prev.stage || "pending",
                ...patch, updatedAt: Date.now(),
            }, { merge: true });
            if (label) logAction(label, [{ col: COL_ASSIGN, id: g.key, prev: prevSnap }]);
        } catch (e) { console.log("save assign", e); alert("Could not save. Please try again."); }
    };

    // Single-row assign: meters batana zaruri (fabric ledger ke liye).
    const assignSingle = async (g, tailorName) => {
        if (!tailorName) { await saveAssign(g, { tailor: "" }, `Unassign · ${g.itemName} (${g.invoiceNo})`); return; }
        const raw = window.prompt(
            `Fabric given to ${tailorName} for this ${g.itemName} (meters) — required.\nEnter 0 only if no fabric was handed over (e.g. reassignment).`,
            ""
        );
        if (raw === null) return; // cancel — assignment nahi hota
        const trimmed = String(raw).trim();
        const meters = num(trimmed);
        if (trimmed === "" || !(meters >= 0)) { alert("Assignment cancelled — fabric meters are required."); return; }
        const undoOps = [{ col: COL_ASSIGN, id: g.key, prev: assignments[g.key] ? stripId({ ...assignments[g.key] }) : null }];
        await saveAssign(g, { tailor: tailorName });
        if (meters > 0) {
            try {
                const issueRef = db.collection(COL_FABRIC_ISSUES).doc();
                undoOps.push({ col: COL_FABRIC_ISSUES, id: issueRef.id, prev: null });
                await issueRef.set({
                    tailor: tailorName,
                    fabric: g.fabric || "(No fabric)",
                    shade: g.shade || "",
                    meters,
                    pieces: 1,
                    note: `Single assign · ${g.itemName} (${g.invoiceNo})`,
                    date: new Date().toISOString(),
                    createdAt: Date.now(),
                });
            } catch (e) { console.log("single issue", e); }
        }
        logAction(`Assign ${g.itemName} (${g.invoiceNo}) → ${tailorName}`, undoOps);
    };

    const rows = useMemo(() => {
        const q = search.trim().toLowerCase();
        return garments
            .map((g) => ({ ...g, a: assignments[g.key] || {} }))
            .filter((g) => {
                if (tailorFilter === "Unassigned") { if (g.a.tailor) return false; }
                else if (tailorFilter && (g.a.tailor || "") !== tailorFilter) return false;
                if (stageFilter && normalizeAssignmentStage(g.a.stage) !== stageFilter) return false;
                if (!q) return true;
                return [g.invoiceNo, g.customerName, g.itemName, g.fabric, g.a.tailor]
                    .filter(Boolean).map((x) => String(x).toLowerCase()).some((p) => p.includes(q));
            });
    }, [garments, assignments, tailorFilter, stageFilter, search]);

    const summary = useMemo(() => {
        const byTailor = {}; let packed = 0; let assigned = 0;
        garments.forEach((g) => {
            const a = assignments[g.key] || {};
            const t = a.tailor || "Unassigned";
            if (!byTailor[t]) byTailor[t] = { total: 0, packed: 0 };
            byTailor[t].total += 1;
            if (a.tailor) assigned += 1;
            const st = normalizeAssignmentStage(a.stage);
            if (isTailorPackedStage(st) || isCollectedOrDoneStage(st)) {
                byTailor[t].packed += 1;
                packed += 1;
            }
        });
        return { byTailor, packed, assigned, total: garments.length };
    }, [garments, assignments]);

    // Order-level: kitne orders ka (tie chhodkar) SARA saman bat gaya.
    const orderDist = useMemo(() => {
        const m = new Map();
        garments.forEach((g) => {
            if (g.itemType === "tie") return;
            if (!m.has(g.bookingId)) m.set(g.bookingId, { invoiceNo: g.invoiceNo, customerName: g.customerName, total: 0, assigned: 0, missing: [] });
            const o = m.get(g.bookingId);
            o.total += 1;
            if (assignments[g.key]?.tailor) o.assigned += 1;
            else o.missing.push(g.itemName);
        });
        const orders = [...m.values()];
        const complete = orders.filter((o) => o.assigned === o.total).length;
        const incomplete = orders
            .filter((o) => o.assigned < o.total)
            .sort((a, b) => (b.assigned / b.total) - (a.assigned / a.total));
        return { totalOrders: orders.length, complete, incomplete };
    }, [garments, assignments]);

    // Har garment type ka hisaab: kitne hain, kitne distribute (tailor assigned), kitne packed
    const typeStats = useMemo(() => {
        const m = {};
        GARMENT_TYPES.forEach((t) => { m[t] = { total: 0, assigned: 0, packed: 0 }; });
        garments.forEach((g) => {
            const s = m[g.itemType];
            if (!s) return;
            s.total += 1;
            const a = assignments[g.key] || {};
            if (a.tailor) s.assigned += 1;
            const st = normalizeAssignmentStage(a.stage);
            if (isTailorPackedStage(st) || isCollectedOrDoneStage(st)) s.packed += 1;
        });
        return m;
    }, [garments, assignments]);

    // Popup ke liye: chune gaye type ke UNASSIGNED pieces, fabric+shade wise
    // (pieces {booking, item} shape me — naap PDF ke liye).
    const remainingByFabric = useMemo(() => {
        if (!typePopup) return [];
        const m = new Map();
        garments.forEach((g) => {
            if (g.itemType !== typePopup) return;
            const a = assignments[g.key] || {};
            if (a.tailor) return; // distribute ho chuka
            const booking = bookingsById[g.bookingId];
            if (!booking) return;
            const item = (booking.active_items || []).find((i) => i.id === g.itemId);
            if (!item) return;
            const k = `${fabricKey(g.fabric) || "—"}||${fabricKey(g.shade)}`;
            if (!m.has(k)) m.set(k, { key: k, name: g.fabric || "(No fabric)", shade: g.shade || "", pieces: [] });
            m.get(k).pieces.push({ booking, item });
        });
        return [...m.values()].sort((a, b) => b.pieces.length - a.pieces.length);
    }, [typePopup, garments, assignments, bookingsById]);

    const allRemainingPieces = useMemo(
        () => remainingByFabric.flatMap((r) => r.pieces),
        [remainingByFabric]
    );

    // Fabric-purchase list: chune type ke DISTRIBUTED (tailor-assigned) pieces,
    // fabric+shade wise grouped — taaki har fabric ka kitna kapda chahiye pata chale.
    const distributedByFabric = useMemo(() => {
        if (!purchasePopup) return [];
        const m = new Map();
        garments.forEach((g) => {
            if (g.itemType !== purchasePopup) return;
            const a = assignments[g.key] || {};
            if (!a.tailor) return; // sirf distribute ho chuke
            const k = `${fabricKey(g.fabric) || "—"}||${fabricKey(g.shade)}`;
            if (!m.has(k)) m.set(k, { key: k, name: g.fabric || "(No fabric)", shade: g.shade || "", pieces: [] });
            m.get(k).pieces.push({ invoiceNo: g.invoiceNo, customerName: g.customerName, tailor: a.tailor });
        });
        const invSort = (a, b) => String(a.invoiceNo).localeCompare(String(b.invoiceNo), undefined, { numeric: true });
        const out = [...m.values()];
        out.forEach((r) => r.pieces.sort(invSort));
        return out.sort((a, b) => b.pieces.length - a.pieces.length);
    }, [purchasePopup, garments, assignments]);

    const distributedTotal = useMemo(
        () => distributedByFabric.reduce((n, r) => n + r.pieces.length, 0),
        [distributedByFabric]
    );

    if (!garments.length) return <div className="campus-orders-empty">No garments to assign yet.</div>;

    return (
        <div className="campus-panel">
            <div className="campus-stat-row">
                <div className="campus-stat campus-stat--accent"><span className="campus-stat-label">Total garments</span><span className="campus-stat-value">{summary.total}</span></div>
                <div className="campus-stat campus-stat--gold">
                    <span className="campus-stat-label">Distributed (given to tailors)</span>
                    <span className="campus-stat-value">{summary.assigned}<small className="campus-stat-sub"> · {summary.total ? Math.round((summary.assigned / summary.total) * 100) : 0}%</small></span>
                </div>
                <div className="campus-stat"><span className="campus-stat-label">Remaining to distribute</span><span className="campus-stat-value">{summary.total - summary.assigned}</span></div>
                <div className="campus-stat campus-stat--good"><span className="campus-stat-label">Packed (ready)</span><span className="campus-stat-value">{summary.packed}</span></div>
                <div className="campus-stat campus-stat--click" role="button" tabIndex={0}
                    title="Click to see which orders still have items left to distribute"
                    onClick={() => setOrdersPopup(true)}
                    onKeyDown={(e) => { if (e.key === "Enter") setOrdersPopup(true); }}>
                    <span className="campus-stat-label">Orders fully distributed (excl. tie)</span>
                    <span className="campus-stat-value">
                        {orderDist.complete} / {orderDist.totalOrders}
                        <small className="campus-stat-sub"> · {orderDist.totalOrders ? Math.round((orderDist.complete / orderDist.totalOrders) * 100) : 0}%</small>
                    </span>
                    <span className="campus-type-more">Details ›</span>
                </div>
            </div>

            <div className="campus-type-row">
                {GARMENT_TYPES.filter((t) => t !== "tie" && typeStats[t].total > 0).map((t) => {
                    const s = typeStats[t];
                    const pct = s.total ? Math.round((s.assigned / s.total) * 100) : 0;
                    return (
                        <div
                            className="campus-type-card campus-type-card--click"
                            key={t}
                            role="button"
                            tabIndex={0}
                            title={`${GARMENT_LABEL[t]}  — view fabric-wise remaining / download measurement PDFs`}
                            onClick={() => setTypePopup(t)}
                            onKeyDown={(e) => { if (e.key === "Enter") setTypePopup(t); }}
                        >
                            <div className="campus-type-head">
                                <strong>{GARMENT_LABEL[t]}</strong>
                                <span>{s.total} pcs</span>
                            </div>
                            <div className="campus-type-nums">
                                <b style={{ color: TYPE_COLOR[t] }}>{s.assigned}</b> distributed · {s.total - s.assigned} remaining
                            </div>
                            <div className="campus-type-bar">
                                <div style={{ width: `${pct}%`, background: TYPE_COLOR[t] }} />
                            </div>
                            <div className="campus-type-foot">{pct}% distributed · {s.packed} packed <span className="campus-type-more">Details ›</span></div>
                        </div>
                    );
                })}
            </div>

            <div className="campus-purchase-bar">
                <span className="campus-purchase-bar-label">Fabric purchase — distributed pieces:</span>
                {GARMENT_TYPES.filter((t) => t !== "tie" && typeStats[t].assigned > 0).map((t) => (
                    <button key={t} type="button" className="campus-purchase-chip" onClick={() => setPurchasePopup(t)}>
                        🧵 {GARMENT_LABEL[t]} list <em>{typeStats[t].assigned}</em>
                    </button>
                ))}
            </div>

            {purchasePopup && (
                <div className="campus-detail-overlay" onClick={() => setPurchasePopup(null)}>
                    <div className="campus-detail-modal campus-purchase-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="campus-detail-header">
                            <div>
                                <div className="campus-detail-kicker">Distributed — for fabric purchase (number · name · shade)</div>
                                <h3>{GARMENT_LABEL[purchasePopup]} · {distributedTotal} distributed · {distributedByFabric.length} fabric{distributedByFabric.length !== 1 ? "s" : ""}</h3>
                            </div>
                            <div className="campus-detail-header-right">
                                <button type="button" className="campus-detail-close" onClick={() => setPurchasePopup(null)} aria-label="Close">&times;</button>
                            </div>
                        </div>
                        <div className="campus-detail-body">
                            {!distributedByFabric.length ? (
                                <div className="campus-orders-empty">No {GARMENT_LABEL[purchasePopup].toLowerCase()} distributed yet.</div>
                            ) : (
                                distributedByFabric.map((r) => (
                                    <div className="campus-purchase-group" key={r.key}>
                                        <div className="campus-purchase-group-head">
                                            <strong>{r.name}</strong>
                                            {r.shade && <span className="campus-shade-pill">{r.shade}</span>}
                                            <span className="campus-purchase-group-count">{r.pieces.length} pcs</span>
                                        </div>
                                        <div className="campus-purchase-pieces">
                                            {r.pieces.map((p, i) => (
                                                <div className="campus-purchase-piece" key={`${p.invoiceNo}-${i}`}>
                                                    <strong>{p.invoiceNo}</strong>
                                                    <span>{p.customerName}</span>
                                                    {p.tailor && <em className="campus-purchase-tailor">{p.tailor}</em>}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            )}

            {ordersPopup && (
                <div className="campus-detail-overlay" onClick={() => setOrdersPopup(false)}>
                    <div className="campus-detail-modal campus-remaining-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="campus-detail-header">
                            <div>
                                <div className="campus-detail-kicker">Orders with items left to distribute (tie excluded)</div>
                                <h3>{orderDist.incomplete.length} incomplete · {orderDist.complete} complete</h3>
                            </div>
                            <div className="campus-detail-header-right">
                                <button type="button" className="campus-detail-close" onClick={() => setOrdersPopup(false)} aria-label="Close">&times;</button>
                            </div>
                        </div>
                        <div className="campus-detail-body">
                            {!orderDist.incomplete.length ? (
                                <div className="campus-orders-empty">Every order is fully distributed 🎉</div>
                            ) : (
                                orderDist.incomplete.map((o) => (
                                    <div className="campus-remaining-row" key={o.invoiceNo}>
                                        <div className="campus-remaining-info">
                                            <strong>{o.invoiceNo}</strong>
                                            <span className="campus-order-by">{o.customerName}</span>
                                        </div>
                                        <span className="campus-remaining-count">{o.assigned}/{o.total} done</span>
                                        <span className="campus-missing">{o.missing.join(", ")} left</span>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            )}

            {typePopup && (
                <div className="campus-detail-overlay" onClick={() => setTypePopup(null)}>
                    <div className="campus-detail-modal campus-remaining-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="campus-detail-header">
                            <div>
                                <div className="campus-detail-kicker">Remaining (unassigned) — by fabric</div>
                                <h3>{GARMENT_LABEL[typePopup]} · {allRemainingPieces.length} remaining</h3>
                            </div>
                            <div className="campus-detail-header-right">
                                {allRemainingPieces.length > 0 && (
                                    <button type="button" className="campus-detail-invoice"
                                        onClick={() => generateFabricTailorSheet(null, "", typePopup, allRemainingPieces)}>
                                        📄 All remaining ({allRemainingPieces.length})
                                    </button>
                                )}
                                <button type="button" className="campus-detail-close" onClick={() => setTypePopup(null)} aria-label="Close">&times;</button>
                            </div>
                        </div>
                        <div className="campus-detail-body">
                            {!remainingByFabric.length ? (
                                <div className="campus-orders-empty">All {GARMENT_LABEL[typePopup].toLowerCase()} pieces have been distributed 🎉</div>
                            ) : (
                                remainingByFabric.map((r) => (
                                    <div className="campus-remaining-row" key={r.key}>
                                        <div className="campus-remaining-info">
                                            <strong>{r.name}</strong>
                                            {r.shade && <span className="campus-shade-pill">{r.shade}</span>}
                                        </div>
                                        <span className="campus-remaining-count">{r.pieces.length} pcs</span>
                                        <button type="button" className="campus-sheet-btn"
                                            onClick={() => generateFabricTailorSheet(r.name, r.shade, typePopup, r.pieces)}>
                                            📄 Measurement PDF
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            )}

            <div className="campus-table-title">Bulk assign — assign a tailor by fabric</div>
            <div className="campus-bulk">
                <div className="campus-bulk-controls">
                    <select className="campus-orders-select" value={bulkFab}
                        onChange={(e) => { setBulkFab(e.target.value); setBulkType(""); setSelected(new Set()); }}>
                        <option value="">— Select fabric —</option>
                        {fabricOptions.map((f) => (
                            <option key={f.key} value={f.key}>{f.name}{f.shade ? ` · Shade ${f.shade}` : ""}</option>
                        ))}
                    </select>
                    {bulkFab && (
                        <div className="campus-bulk-types">
                            {bulkTypes.map(([t, c]) => (
                                <button key={t} type="button"
                                    className={`campus-type-chip${bulkType === t ? " is-active" : ""}`}
                                    onClick={() => { setBulkType(t); setSelected(new Set()); }}>
                                    {GARMENT_LABEL[t]} ({c})
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {bulkFab && bulkType && (
                    <>
                        <div className="campus-bulk-list">
                            <label className="campus-bulk-item campus-bulk-allrow">
                                <input type="checkbox" checked={allChecked} onChange={toggleAll} />
                                <span className="cb-all">Select all ({bulkList.length})</span>
                            </label>
                            {bulkList.map((g) => {
                                const a = assignments[g.key] || {};
                                return (
                                    <label key={g.key} className="campus-bulk-item">
                                        <input type="checkbox" checked={selected.has(g.key)} onChange={() => toggleOne(g.key)} />
                                        <span className="cb-inv">{g.invoiceNo}</span>
                                        <span className="cb-name">{g.customerName}</span>
                                        <span className={`cb-tailor${a.tailor ? " has" : ""}`}>{a.tailor ? `→ ${a.tailor}` : "unassigned"}</span>
                                    </label>
                                );
                            })}
                        </div>
                        <div className="campus-bulk-assign">
                            <select value={bulkTailor} onChange={(e) => setBulkTailor(e.target.value)}>
                                <option value="">— Select tailor —</option>
                                {activeTailorNames.map((n) => <option key={n} value={n}>{n}</option>)}
                            </select>
                            <input type="number" min="0" step="0.5" className="campus-bulk-meters"
                                placeholder="Fabric given (meters) — required"
                                value={bulkMeters} onChange={(e) => setBulkMeters(e.target.value)} />
                            <button type="button" className="campus-bulk-btn"
                                disabled={!selected.size || !bulkTailor.trim() || String(bulkMeters).trim() === ""} onClick={assignBulk}>
                                Assign {selected.size || ""} → Tailor
                            </button>
                        </div>
                        {bulkTailor && tailorFabricLeft !== null && (
                            <p className="campus-bulk-hint">
                                <span className={`campus-stock ${tailorFabricLeft > 0 ? "campus-stock--ok" : "campus-stock--neg"}`}>
                                    {bulkTailor} holds {fmtM(Math.max(0, tailorFabricLeft))} of this fabric
                                </span>
                                {selected.size > 0 && bulkType && (
                                    <span className="campus-bulk-need"> · this selection needs ~{fmtM(selected.size * (consumption[bulkType] || 0))}</span>
                                )}
                            </p>
                        )}
                        <p className="campus-note">Fabric meters are required — enter 0 if the tailor will cut from fabric already with them. Every entry goes to the fabric ledger (Tailors tab).</p>
                        {activeTailorNames.length === 0 && (
                            <p className="campus-note">No active tailors — add them in the "Tailors" tab.</p>
                        )}
                    </>
                )}
            </div>

            <div className="campus-table-title">Workload per tailor</div>
            <div className="campus-tailor-chips">
                {Object.entries(summary.byTailor).sort((a, b) => b[1].total - a[1].total).map(([t, v]) => (
                    <button type="button"
                        className={`campus-tailor-chip campus-tailor-chip--btn ${tailorFilter === t ? "is-active" : ""}`}
                        key={t}
                        onClick={() => setTailorFilter(tailorFilter === t ? "" : t)}>
                        <strong>{t}</strong>
                        <span>{v.total} pcs · {v.packed} packed</span>
                    </button>
                ))}
            </div>
            <p className="campus-note">Click a tailor to see only their pieces and update stages.</p>

            <div className="campus-orders-toolbar campus-dist-toolbar">
                <div className="campus-orders-search">
                    <input type="search" placeholder="Search invoice, customer, item, fabric, tailor…" value={search}
                        onChange={(e) => setSearch(e.target.value)} autoComplete="off" />
                </div>
                <select className="campus-orders-select" value={tailorFilter} onChange={(e) => setTailorFilter(e.target.value)}>
                    <option value="">All tailors</option>
                    <option value="Unassigned">Unassigned</option>
                    {tailorList.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <select className="campus-orders-select" value={stageFilter} onChange={(e) => setStageFilter(e.target.value)}>
                    <option value="">All stages</option>
                    {STAGES.map((s) => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                </select>
            </div>

            {!(search.trim().length >= 2 || tailorFilter || stageFilter) ? (
                <div className="campus-orders-empty">Select a tailor above, or use search / filters, to see pieces here.</div>
            ) : !rows.length ? (
                <div className="campus-orders-empty">No pieces match this filter.</div>
            ) : (
            <div className="campus-table-wrap">
                <table className="campus-table">
                    <thead>
                        <tr><th>Invoice</th><th>Customer</th><th>Item</th><th>Fabric</th><th>Tailor</th><th>Stage</th></tr>
                    </thead>
                    <tbody>
                        {rows.map((g) => (
                            <tr key={g.key} className={isCollectedOrDoneStage(g.a.stage) ? "campus-row-packed" : isTailorPackedStage(g.a.stage) ? "campus-row-packed" : ""}>
                                <td>{g.invoiceNo}</td>
                                <td>{g.customerName}</td>
                                <td><strong>{g.itemName}</strong></td>
                                <td>{g.fabric || "—"}</td>
                                <td>
                                    <select className="campus-tailor-input" value={g.a.tailor || ""}
                                        onChange={(e) => assignSingle(g, e.target.value)}>
                                        <option value="">Assign…</option>
                                        {g.a.tailor && !activeTailorNames.includes(g.a.tailor) &&
                                            <option value={g.a.tailor}>{g.a.tailor}</option>}
                                        {activeTailorNames.map((n) => <option key={n} value={n}>{n}</option>)}
                                    </select>
                                </td>
                                <td>
                                    <select className={`campus-stage-select campus-stage--${g.a.stage || "pending"}`}
                                        value={g.a.stage || "pending"} onChange={(e) => saveAssign(g, { stage: e.target.value }, `Stage → ${e.target.value} · ${g.itemName} (${g.invoiceNo})`)}>
                                        {STAGES.map((s) => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                                    </select>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            )}
            <p className="campus-note">Stage &quot;Packed&quot; = at tailor. &quot;Received&quot; / &quot;Box packed&quot; = collected or admin packed (shows in Tailors → Received).</p>
        </div>
    );
}

export default Campus_orders;
